import * as React from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography
} from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded'

import { ErrorType, ActionType } from '../types.js'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { resolveAttackEntryArrivalTime, resolveMovementArrivalTime } from '../utils/attackMovementTiming'
import { resolveRuntimeUptimeMs } from '../utils/runtimeStatus'
import OfferOverviewCard from './OfferOverviewCard'
import BthSkipInventoryCard, { isBthAccount } from './BthSkipInventoryCard'
import TroopStockUnitChip from './TroopStockUnitChip'
import {
    resolveIncomingKhanTauntCount,
    resolveRecentKhanAttackCount
} from '../utils/khanTauntDetection'

const numberFormatter = new Intl.NumberFormat('en-US')

const CASTLE_DEFENSE_MANUAL_LOCATIONS = Object.freeze([
    { key: 'main', label: 'Main Castle' },
    { key: 'outpost1', label: 'Outpost 1' },
    { key: 'outpost2', label: 'Outpost 2' },
    { key: 'outpost3', label: 'Outpost 3' },
    { key: 'ice', label: 'Ice' },
    { key: 'desert', label: 'Desert' },
    { key: 'fire', label: 'Fire' },
    { key: 'storm', label: 'Storm' },
    { key: 'capital', label: 'Capital' },
    { key: 'metro', label: 'Metro' }
])

const resolveLiveCastleName = entry =>
    String(entry?.nameCandidate ?? entry?.castleName ?? '').trim()

export const resolveCastleDefenseLocationDisplayName = (status, location) => {
    const locationKey = String(location?.key ?? '').trim()
    const genericLabel = String(location?.label ?? locationKey).trim()
    const liveEntries = [
        ...(Array.isArray(status?.barracksTargets) ? status.barracksTargets : []),
        ...(Array.isArray(status?.troopStock?.castles) ? status.troopStock.castles : [])
    ]
    const matchedEntry = liveEntries.find(entry => String(entry?.locationKey ?? '').trim() === locationKey)
    const castleName = resolveLiveCastleName(matchedEntry)

    return castleName && castleName.toLocaleLowerCase() !== genericLabel.toLocaleLowerCase() ?
        `${genericLabel} · ${castleName}` :
        genericLabel
}

const normalizeFiniteNumber = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : undefined
}

const normalizePositiveWholeNumber = value => {
    const numericValue = normalizeFiniteNumber(value)
    return numericValue !== undefined && numericValue > 0 ? Math.floor(numericValue) : undefined
}

const normalizeWholeNumber = value => {
    const numericValue = normalizeFiniteNumber(value)
    return numericValue !== undefined ? Math.floor(numericValue) : undefined
}

const normalizeNonNegativeWholeNumber = value => {
    const numericValue = normalizeFiniteNumber(value)
    return numericValue !== undefined && numericValue >= 0 ? Math.floor(numericValue) : undefined
}

export const formatDashboardWholeNumber = (value, unavailableLabel = 'Unavailable') => {
    const normalizedValue = normalizeWholeNumber(value)
    return normalizedValue !== undefined ? numberFormatter.format(normalizedValue) : unavailableLabel
}

const formatDashboardRank = (value, unavailableLabel = 'Unavailable') => {
    const normalizedValue = normalizePositiveWholeNumber(value)
    return normalizedValue !== undefined ? `#${numberFormatter.format(normalizedValue)}` : unavailableLabel
}

export const buildDashboardDivisionRankingSummary = (status, unavailableLabel = 'Unavailable') => {
    const divisionRank = normalizePositiveWholeNumber(status?.leagueDivisionRank)
    const divisionId = normalizePositiveWholeNumber(status?.leagueDivisionId)
    const divisionSize = normalizePositiveWholeNumber(status?.leagueDivisionSize)

    if (divisionRank === undefined) {
        return {
            value: unavailableLabel,
            caption: "Kingdom's League | Division ranking"
        }
    }

    const captionParts = ["Kingdom's League", 'Division ranking']
    if (divisionId !== undefined)
        captionParts.push(`Division ${numberFormatter.format(divisionId)}`)
    if (divisionSize !== undefined)
        captionParts.push(`${numberFormatter.format(divisionSize)} players`)

    return {
        value: `#${numberFormatter.format(divisionRank)}`,
        caption: captionParts.join(' | ')
    }
}

const buildDashboardKingdomLeagueSummary = (status, unavailableLabel = 'Unavailable') => {
    const seasonRank = normalizePositiveWholeNumber(status?.leagueSeasonRank)

    if (seasonRank !== undefined) {
        return {
            value: `#${numberFormatter.format(seasonRank)}`,
            caption: "Kingdom's League | Season ranking"
        }
    }

    return buildDashboardDivisionRankingSummary(status, unavailableLabel)
}

const buildDashboardBerriWorldSummary = (status, unavailableLabel = 'Unavailable') => {
    const rank = normalizePositiveWholeNumber(status?.berriWorldRank)

    return {
        value: rank !== undefined ? `#${numberFormatter.format(rank)}` : unavailableLabel,
        caption: 'Battle for Berimond | Bravery ranking'
    }
}

const resolveDashboardKingdomLeagueTopEntries = status =>
    Array.isArray(status?.leagueSeasonLeaderboard) ?
        status.leagueSeasonLeaderboard
            .map(entry => ({
                rank: normalizePositiveWholeNumber(entry?.rank),
                points: normalizeWholeNumber(entry?.points),
                playerName: String(entry?.playerName ?? '').trim(),
                allianceName: String(entry?.allianceName ?? '').trim(),
                goldMedals: normalizeNonNegativeWholeNumber(entry?.goldMedals),
                silverMedals: normalizeNonNegativeWholeNumber(entry?.silverMedals),
                bronzeMedals: normalizeNonNegativeWholeNumber(entry?.bronzeMedals)
            }))
            .filter(entry => entry.rank !== undefined && entry.playerName.length > 0)
            .sort((left, right) => Number(left.rank) - Number(right.rank))
            .slice(0, 6) :
        []

const resolveDashboardBerriWorldTopEntries = status =>
    Array.isArray(status?.berriWorldLeaderboard) ?
        status.berriWorldLeaderboard
            .map(entry => ({
                rank: normalizePositiveWholeNumber(entry?.rank),
                points: normalizeWholeNumber(entry?.points),
                playerName: String(entry?.playerName ?? '').trim(),
                allianceName: String(entry?.allianceName ?? '').trim()
            }))
            .filter(entry => entry.rank !== undefined && entry.playerName.length > 0)
            .sort((left, right) => Number(left.rank) - Number(right.rank))
            .slice(0, 6) :
        []

const grandTournamentDivisionNames = {
    1: 'Copper',
    2: 'Glass',
    3: 'Bronze',
    4: 'Silver',
    5: 'Gold',
    6: 'Diamond'
}

const resolveTournamentEndTimeMs = tournament => {
    const endsAt = normalizeFiniteNumber(tournament?.endsAt)
    if (endsAt === undefined || endsAt <= 0) return undefined
    if (endsAt >= 1e12) return endsAt
    if (endsAt >= 1e9) return endsAt * 1000
    const updatedAt = normalizeFiniteNumber(tournament?.updatedAt)
    return updatedAt !== undefined && updatedAt > 0 ? updatedAt + endsAt * 1000 : undefined
}

const formatTournamentCountdown = remainingMs => {
    if (!Number.isFinite(remainingMs)) return '—'
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':')
}

export const resolveDashboardGrandTournament = (status, now = Date.now()) => {
    const tournament = status?.grandTournament
    if (!tournament?.active) return { active: false, leaderboard: [] }
    const leaderboard = (Array.isArray(tournament.leaderboard) ? tournament.leaderboard : [])
        .map(entry => ({
            rank: normalizePositiveWholeNumber(entry?.rank),
            allianceName: String(entry?.allianceName ?? '').trim(),
            serverInstanceID: normalizeNonNegativeWholeNumber(entry?.serverInstanceID),
            points: normalizeNonNegativeWholeNumber(entry?.points)
        }))
        .filter(entry => entry.rank !== undefined && entry.allianceName)
        .sort((left, right) => left.rank - right.rank)
        .slice(0, 5)
    const endTimeMs = resolveTournamentEndTimeMs(tournament)
    return {
        active: true,
        division: grandTournamentDivisionNames[normalizePositiveWholeNumber(tournament.leagueID)] ?? 'Unranked',
        personalPoints: normalizeNonNegativeWholeNumber(tournament.personalPoints) ?? 0,
        personalRank: normalizePositiveWholeNumber(tournament.personalRank),
        alliancePoints: normalizeNonNegativeWholeNumber(tournament.alliancePoints) ?? 0,
        allianceRank: normalizePositiveWholeNumber(tournament.allianceRank),
        remainingMs: endTimeMs !== undefined ? Math.max(0, endTimeMs - Number(now)) : undefined,
        leaderboard,
        leaderboardUpdatedAt: normalizePositiveWholeNumber(tournament.leaderboardUpdatedAt),
        leaderboardError: String(tournament.leaderboardError ?? '').trim()
    }
}

export const resolveDashboardAllianceRift = (status, now = Date.now()) => {
    const portal = status?.allianceRiftPortal
    if (!portal?.active) return { active: false, leaderboard: [] }
    const leaderboard = (Array.isArray(portal.leaderboard) ? portal.leaderboard : [])
        .map(entry => ({
            rank: normalizePositiveWholeNumber(entry?.rank),
            allianceName: String(entry?.allianceName ?? '').trim(),
            serverInstanceID: normalizeNonNegativeWholeNumber(entry?.serverInstanceID),
            points: normalizeNonNegativeWholeNumber(entry?.points)
        }))
        .filter(entry => entry.rank !== undefined && entry.allianceName)
        .sort((left, right) => left.rank - right.rank)
        .slice(0, 5)
    const questEndTimeMs = resolveTournamentEndTimeMs(portal.questEvent)
    const normalizeWall = wall => ({
        percentLeft: Math.max(0, Math.min(100, normalizeNonNegativeWholeNumber(wall?.percentLeft) ?? 0)),
        regenerationSeconds: normalizeNonNegativeWholeNumber(wall?.regenerationSeconds) ?? 0
    })
    return {
        active: true,
        bossLevelID: normalizeNonNegativeWholeNumber(portal.bossLevelID) ?? 0,
        bossStageID: normalizeNonNegativeWholeNumber(portal.bossStageID) ?? 0,
        health: Math.max(0, Math.min(100, normalizeNonNegativeWholeNumber(portal.health) ?? 0)),
        playerPoints: normalizeNonNegativeWholeNumber(portal.playerPoints) ?? 0,
        bossPointTarget: normalizeNonNegativeWholeNumber(portal.bossPointTarget) ?? 0,
        sentCount: normalizeNonNegativeWholeNumber(portal.sentCount) ?? 0,
        outboundAttackCount: Array.isArray(portal.outboundAttacks) ? portal.outboundAttacks.length : 0,
        allianceQuestsActive: Boolean(portal.allianceQuestsActive ?? portal.questEvent?.active),
        remainingMs: questEndTimeMs !== undefined ? Math.max(0, questEndTimeMs - Number(now)) : undefined,
        walls: {
            left: normalizeWall(portal?.walls?.left),
            middle: normalizeWall(portal?.walls?.middle),
            right: normalizeWall(portal?.walls?.right)
        },
        leaderboard,
        leaderboardError: String(portal.leaderboardError ?? '').trim()
    }
}

function GrandTournamentDashboardCard({ tournament, allianceName, t }) {
    const [expanded, setExpanded] = React.useState(true)
    const topPoints = tournament.leaderboard[0]?.points ?? 0
    const progress = topPoints > 0 ? Math.max(0, Math.min(100, tournament.alliancePoints * 100 / topPoints)) : 0
    const rankColors = ['#ffd43b', '#9dd8ff', '#fb923c', '#22d3ee', '#34d399']
    return (
        <Box
            data-testid="grand-tournament-card"
            sx={{
                overflow: 'hidden',
                borderRadius: 2.2,
                border: '1px solid rgba(250,204,21,0.34)',
                background: 'linear-gradient(145deg, rgba(39,45,61,.98), rgba(20,28,45,.98))',
                boxShadow: '0 18px 44px rgba(3,7,18,.2)'
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.2} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <EmojiEventsRoundedIcon sx={{ color: '#facc15' }} />
                    <Box>
                        <Typography sx={{ color: '#facc15', fontWeight: 950, letterSpacing: '.035em', lineHeight: 1.05 }}>
                            {t('Grand Tournament')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.62)' }}>
                            {t('Alliance ranking')} · {t(tournament.division)}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.15, py: .5, backgroundColor: 'rgba(255,255,255,.06)' }}>
                        <HourglassTopRoundedIcon sx={{ color: '#f8c452', fontSize: 19 }} />
                        <Typography sx={{ color: '#f8c452', fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTournamentCountdown(tournament.remainingMs)}
                        </Typography>
                    </Stack>
                    <Tooltip title={t(expanded ? 'Collapse tournament ranking' : 'Expand tournament ranking')}>
                        <IconButton
                            size="small"
                            aria-label={t(expanded ? 'Collapse tournament ranking' : 'Expand tournament ranking')}
                            aria-expanded={expanded}
                            onClick={() => setExpanded(value => !value)}
                            sx={{ color: '#f8c452' }}
                        >
                            {expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Grid container spacing={0} sx={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                {[
                    [t('Division'), t(tournament.division)],
                    [t('Alliance contribution'), formatDashboardWholeNumber(tournament.alliancePoints, '0')],
                    [t('Alliance rank'), tournament.allianceRank ? `#${numberFormatter.format(tournament.allianceRank)}` : '—'],
                    [t('Personal contribution'), formatDashboardWholeNumber(tournament.personalPoints, '0')]
                ].map(([label, value]) => (
                    <Grid item xs={6} md={3} key={label} sx={{ px: 1.35, py: 1.05, borderRight: '1px solid rgba(255,255,255,.06)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.55)' }}>{label}</Typography>
                        <Typography sx={{ mt: .2, color: '#f8e4c5', fontWeight: 850 }}>{value}</Typography>
                    </Grid>
                ))}
            </Grid>

            {topPoints > 0 ? (
                <Box sx={{ px: 1.4, pt: 1.15 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.58)' }}>{t('Progress against first place')}</Typography>
                        <Typography variant="caption" sx={{ color: '#d9e780', fontWeight: 800 }}>{`${Math.round(progress)}%`}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={progress} sx={{ mt: .55, height: 8, backgroundColor: 'rgba(255,255,255,.08)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#8aa83e,#c6d84b)' } }} />
                </Box>
            ) : null}

            <Box sx={{ p: 1.35 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '3.2rem minmax(0,1fr) minmax(5.5rem,.65fr) minmax(6rem,.7fr)', gap: .8, px: 1.05, pb: .6 }}>
                    {[t('Rank'), t('Alliance name'), t('Server'), t('Total points')].map((label, index) => (
                        <Typography key={label} variant="caption" sx={{ color: '#ead6b7', fontWeight: 800, textAlign: index > 1 ? 'right' : 'left' }}>{label}</Typography>
                    ))}
                </Box>
                {tournament.leaderboard.length ? (
                    <Stack spacing={.42}>
                        {tournament.leaderboard.map((entry, index) => {
                            const ownAlliance = allianceName && entry.allianceName.toLowerCase() === allianceName.toLowerCase()
                            return (
                                <Box key={`${entry.rank}-${entry.allianceName}`} sx={{ display: 'grid', gridTemplateColumns: '3.2rem minmax(0,1fr) minmax(5.5rem,.65fr) minmax(6rem,.7fr)', gap: .8, alignItems: 'center', px: 1.05, py: .75, borderLeft: `4px solid ${rankColors[index] ?? '#64748b'}`, backgroundColor: ownAlliance ? 'rgba(250,204,21,.16)' : 'rgba(242,216,180,.09)' }}>
                                    <Typography sx={{ color: '#f7e7d1', fontWeight: 900 }}>{entry.rank}</Typography>
                                    <Typography noWrap sx={{ color: '#f7e7d1', fontWeight: ownAlliance ? 900 : 750 }}>{entry.allianceName}</Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.66)', textAlign: 'right' }}>{entry.serverInstanceID ? `#${entry.serverInstanceID}` : '—'}</Typography>
                                    <Typography variant="body2" sx={{ color: '#f7e7d1', fontWeight: 800, textAlign: 'right' }}>{formatDashboardWholeNumber(entry.points, '0')}</Typography>
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ py: 2, color: 'rgba(255,255,255,.55)', textAlign: 'center' }}>
                        {tournament.leaderboardError ? t('Tournament ranking is temporarily unavailable') : t('Waiting for tournament ranking data')}
                    </Typography>
                )}
            </Box>
            </Collapse>
        </Box>
    )
}

function AllianceRiftDashboardCard({ portal, allianceName, t }) {
    const rankColors = ['#67e8f9', '#a5b4fc', '#c084fc', '#60a5fa', '#34d399']
    const walls = [
        [t('Left wall'), portal.walls.left],
        [t('Middle wall'), portal.walls.middle],
        [t('Right wall'), portal.walls.right]
    ]
    return (
        <Box data-testid="alliance-rift-card" sx={{ overflow: 'hidden', borderRadius: 2.2, border: '1px solid rgba(103,232,249,.34)', background: 'linear-gradient(145deg,rgba(20,38,62,.98),rgba(23,20,52,.98))', boxShadow: '0 18px 44px rgba(3,7,18,.24)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.2} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ShieldRoundedIcon sx={{ color: '#67e8f9' }} />
                    <Box>
                        <Typography sx={{ color: '#67e8f9', fontWeight: 950, letterSpacing: '.035em', lineHeight: 1.05 }}>{t('Alliance RIFT Portal')}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.62)' }}>{`${t('RIFT boss')} · ${t('Level')} ${portal.bossLevelID}`}</Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={.75} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, px: 1.15, py: .5, backgroundColor: 'rgba(255,255,255,.06)' }}>
                    <HourglassTopRoundedIcon sx={{ color: portal.allianceQuestsActive ? '#67e8f9' : '#94a3b8', fontSize: 19 }} />
                    <Typography sx={{ color: portal.allianceQuestsActive ? '#a5f3fc' : '#cbd5e1', fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                        {portal.allianceQuestsActive ? formatTournamentCountdown(portal.remainingMs) : t('Portal active')}
                    </Typography>
                </Stack>
            </Stack>

            <Grid container spacing={0} sx={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                {[
                    [t('Boss level'), portal.bossLevelID],
                    [t('Portal health'), `${portal.health}%`],
                    [t('RIFT points'), formatDashboardWholeNumber(portal.playerPoints, '0')],
                    [t('Attacks sent'), formatDashboardWholeNumber(portal.sentCount, '0')]
                ].map(([label, value]) => (
                    <Grid item xs={6} md={3} key={label} sx={{ px: 1.35, py: 1.05, borderRight: '1px solid rgba(255,255,255,.06)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.55)' }}>{label}</Typography>
                        <Typography sx={{ mt: .2, color: '#dff8ff', fontWeight: 850 }}>{value}</Typography>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ px: 1.4, pt: 1.15 }}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="caption" sx={{ color: 'rgba(255,255,255,.58)' }}>{t('Portal health')}</Typography><Typography variant="caption" sx={{ color: '#a5f3fc', fontWeight: 800 }}>{`${portal.health}%`}</Typography></Stack>
                <LinearProgress variant="determinate" value={portal.health} sx={{ mt: .55, height: 8, backgroundColor: 'rgba(255,255,255,.08)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#2563eb,#67e8f9)' } }} />
                <Grid container spacing={.8} sx={{ mt: .5 }}>
                    {walls.map(([label, wall]) => (
                        <Grid item xs={4} key={label}>
                            <Box sx={{ p: .75, backgroundColor: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.06)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.55)' }}>{label}</Typography>
                                <Typography variant="body2" sx={{ color: '#dff8ff', fontWeight: 850 }}>{`${wall.percentLeft}%`}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ p: 1.35 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: .65, color: '#c4b5fd', fontWeight: 850 }}>{t('RIFT alliance ranking')}</Typography>
                {portal.leaderboard.length ? (
                    <Stack spacing={.42}>
                        {portal.leaderboard.map((entry, index) => {
                            const ownAlliance = allianceName && entry.allianceName.toLowerCase() === allianceName.toLowerCase()
                            return (
                                <Box key={`${entry.rank}-${entry.allianceName}`} sx={{ display: 'grid', gridTemplateColumns: '3.2rem minmax(0,1fr) minmax(5.5rem,.65fr) minmax(6rem,.7fr)', gap: .8, alignItems: 'center', px: 1.05, py: .75, borderLeft: `4px solid ${rankColors[index] ?? '#64748b'}`, backgroundColor: ownAlliance ? 'rgba(103,232,249,.15)' : 'rgba(255,255,255,.045)' }}>
                                    <Typography sx={{ color: '#e0f2fe', fontWeight: 900 }}>{entry.rank}</Typography>
                                    <Typography noWrap sx={{ color: '#e0f2fe', fontWeight: ownAlliance ? 900 : 750 }}>{entry.allianceName}</Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.66)', textAlign: 'right' }}>{entry.serverInstanceID ? `#${entry.serverInstanceID}` : '—'}</Typography>
                                    <Typography variant="body2" sx={{ color: '#e0f2fe', fontWeight: 800, textAlign: 'right' }}>{formatDashboardWholeNumber(entry.points, '0')}</Typography>
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ py: 1.5, color: 'rgba(255,255,255,.55)', textAlign: 'center' }}>
                        {portal.allianceQuestsActive ? (portal.leaderboardError ? t('RIFT ranking is temporarily unavailable') : t('Waiting for RIFT ranking data')) : t('RIFT ranking appears during alliance quests')}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export const resolveDashboardCurrencies = status => ({
    coins: normalizeWholeNumber(status?.coin ?? status?.coins),
    rubies: normalizeWholeNumber(status?.rubies),
    aquamarine: normalizeWholeNumber(status?.aquamarine),
    aquamarineSpent: normalizeWholeNumber(status?.stormAquaPoints ?? status?.playerAquaPoints ?? status?.aquamarineSpent),
    internalContestRank: normalizePositiveWholeNumber(status?.stormInternalRank ?? status?.currentPlayerStormRank ?? status?.internalAllianceContestRank),
    internalContestPoints: normalizeWholeNumber(status?.stormAquaPoints ?? status?.playerAquaPoints ?? status?.internalAllianceContestPoints),
    allianceStormRank: normalizePositiveWholeNumber(status?.stormAllianceRank ?? status?.currentAllianceStormRank)
})

const BTH_MODULE_NAMES = {
    bth_influence_hunter: 'BTH Influence Hunter',
    bth_influence_attack: 'BTH Influence Attacks',
    bth_tax_collector: 'BTH Automatic Tax Collector',
    bth_map_attack: 'BTH Map Attack',
    equipment_seller: 'Equipment Seller',
    attackbarronsempire: 'Tower Bot'
}

export const getDashboardActivePluginNames = (userPlugins, plugins, botSettings, bthMode = false) => {
    const visiblePlugins = Array.isArray(plugins) ? plugins.filter(plugin => bthMode || !plugin?.hidden) : []
    const names = Object.entries(userPlugins || {})
        .filter(([, value]) => value?.state)
        .map(([key]) => visiblePlugins.find(plugin => plugin.key === key)?.name || (bthMode ? BTH_MODULE_NAMES[key] : undefined))
        .filter(Boolean)

    if (botSettings?.enableAllianceRiftPortal)
        names.push('Alliance RIFT Portal')

    if (botSettings?.enableGrandTournament)
        names.push('Grand Tournament')

    return [...new Set(names)]
}

export const resolveDashboardResourceAlert = (status, now = Date.now()) => {
    const alerts = Array.isArray(status?.resourceAlerts) ? status.resourceAlerts.filter(Boolean) : []
    const lastSeenAt = normalizeNonNegativeWholeNumber(status?.lastSeenAt)
    const normalizedAlerts = alerts.map(alert => {
        const depletionEtaMs = normalizeNonNegativeWholeNumber(alert?.depletionEtaMs)
        const depletionAt = lastSeenAt !== undefined && depletionEtaMs !== undefined ?
            lastSeenAt + depletionEtaMs :
            undefined
        const depletionRemainingMs = depletionAt !== undefined ?
            Math.max(0, depletionAt - now) :
            undefined

        return {
            castleLabel: String(alert?.castleLabel ?? '').trim(),
            resourceLabel: String(alert?.resourceLabel ?? '').trim(),
            delta: normalizeFiniteNumber(alert?.delta),
            currentAmount: normalizeNonNegativeWholeNumber(alert?.currentAmount),
            depletionEtaMs,
            ...(depletionAt !== undefined ? { depletionAt } : {}),
            ...(depletionRemainingMs !== undefined ? { depletionRemainingMs } : {})
        }
    }).sort((left, right) => {
        const leftEta = normalizeNonNegativeWholeNumber(left.depletionRemainingMs ?? left.depletionEtaMs)
        const rightEta = normalizeNonNegativeWholeNumber(right.depletionRemainingMs ?? right.depletionEtaMs)
        const normalizedLeftEta = leftEta === undefined ? Number.MAX_SAFE_INTEGER : leftEta
        const normalizedRightEta = rightEta === undefined ? Number.MAX_SAFE_INTEGER : rightEta

        return normalizedLeftEta - normalizedRightEta ||
            Number(left.currentAmount ?? Number.MAX_SAFE_INTEGER) - Number(right.currentAmount ?? Number.MAX_SAFE_INTEGER) ||
            Number(left.delta ?? 0) - Number(right.delta ?? 0) ||
            String(left.castleLabel ?? '').localeCompare(String(right.castleLabel ?? '')) ||
            String(left.resourceLabel ?? '').localeCompare(String(right.resourceLabel ?? ''))
    })
    const primaryAlert = normalizedAlerts[0]

    if (!primaryAlert) {
        return {
            hasAlert: false,
            castleLabel: '',
            resourceLabel: '',
            delta: undefined,
            currentAmount: undefined,
            depletionEtaMs: undefined,
            depletionRemainingMs: undefined,
            extraCount: 0,
            details: []
        }
    }

    return {
        hasAlert: true,
        castleLabel: String(primaryAlert?.castleLabel ?? '').trim(),
        resourceLabel: String(primaryAlert?.resourceLabel ?? '').trim(),
        delta: normalizeFiniteNumber(primaryAlert?.delta),
        currentAmount: normalizeNonNegativeWholeNumber(primaryAlert?.currentAmount),
        depletionEtaMs: normalizeNonNegativeWholeNumber(primaryAlert?.depletionEtaMs),
        ...(primaryAlert.depletionAt !== undefined ? { depletionAt: primaryAlert.depletionAt } : {}),
        ...(primaryAlert.depletionRemainingMs !== undefined ? { depletionRemainingMs: primaryAlert.depletionRemainingMs } : {}),
        extraCount: Math.max(0, alerts.length - 1),
        details: normalizedAlerts
    }
}

export const buildDashboardResourceAlertCaption = (resourceAlert, unavailableLabel = 'Unavailable') => {
    if (!resourceAlert?.hasAlert)
        return 'Food | Honey | Mead'

    const primaryCastleLabel = String(resourceAlert?.castleLabel ?? '').trim()
    const details = Array.isArray(resourceAlert?.details) && resourceAlert.details.length > 0 ?
        resourceAlert.details :
        [{
            castleLabel: primaryCastleLabel,
            resourceLabel: String(resourceAlert?.resourceLabel ?? '').trim(),
            delta: normalizeFiniteNumber(resourceAlert?.delta)
        }]

    return details
        .map((detail, index) => {
            const detailCastleLabel = String(detail?.castleLabel ?? '').trim()
            const resourceLabel = String(detail?.resourceLabel ?? '').trim() || unavailableLabel
            const rateLabel =
                detail?.delta !== undefined ?
                    `${numberFormatter.format(detail.delta)}/h` :
                    unavailableLabel
            const shouldShowCastleLabel = Boolean(detailCastleLabel) && detailCastleLabel !== primaryCastleLabel

            return shouldShowCastleLabel ?
                `${detailCastleLabel}: ${resourceLabel} ${rateLabel}` :
                `${resourceLabel} ${rateLabel}`
        })
        .join(' | ')
}

const normalizeDashboardTroopUnit = unit => {
    const unitID = normalizeWholeNumber(unit?.unitID)
    const amount = normalizeNonNegativeWholeNumber(unit?.amount)
    if (unitID === undefined || amount === undefined || amount <= 0)
        return undefined

    return {
        unitID,
        amount,
        label: String(unit?.label ?? `Unit ${unitID}`).trim() || `Unit ${unitID}`,
        role: String(unit?.role ?? '').trim(),
        side: String(unit?.side ?? '').trim(),
        iconUrl: String(unit?.iconUrl ?? '').trim(),
        troopLevel: normalizePositiveWholeNumber(unit?.troopLevel)
    }
}

const normalizeDashboardTroopCastle = castle => {
    const units = (Array.isArray(castle?.units) ? castle.units : [])
        .map(normalizeDashboardTroopUnit)
        .filter(Boolean)
        .sort((left, right) =>
            Number(right.amount) - Number(left.amount) ||
            String(left.label).localeCompare(String(right.label)) ||
            Number(left.unitID) - Number(right.unitID))
    const total = normalizeNonNegativeWholeNumber(castle?.total) ??
        units.reduce((sum, unit) => sum + unit.amount, 0)
    const stronghold = normalizeNonNegativeWholeNumber(castle?.stronghold) ?? 0
    const hospital = normalizeNonNegativeWholeNumber(castle?.hospital) ?? 0
    const traveling = normalizeNonNegativeWholeNumber(castle?.traveling) ?? 0

    if (total <= 0 && stronghold <= 0 && hospital <= 0 && traveling <= 0)
        return undefined

    return {
        kingdomID: normalizeWholeNumber(castle?.kingdomID),
        areaID: normalizeWholeNumber(castle?.areaID),
        areaType: normalizeWholeNumber(castle?.areaType),
        x: normalizeWholeNumber(castle?.x),
        y: normalizeWholeNumber(castle?.y),
        castleLabel: String(castle?.castleLabel ?? '').trim() || 'Unknown castle',
        total,
        attack: normalizeNonNegativeWholeNumber(castle?.attack) ?? 0,
        defense: normalizeNonNegativeWholeNumber(castle?.defense) ?? 0,
        melee: normalizeNonNegativeWholeNumber(castle?.melee) ?? 0,
        ranged: normalizeNonNegativeWholeNumber(castle?.ranged) ?? 0,
        stronghold,
        hospital,
        traveling,
        units
    }
}

const DASHBOARD_AREA_TYPE = Object.freeze({
    mainCastle: 1,
    capital: 3,
    outpost: 4,
    externalKingdom: 12,
    metropolis: 22
})

const DASHBOARD_KINGDOM_ID = Object.freeze({
    greatEmpire: 0,
    burningSands: 1,
    everWinterGlacier: 2,
    firePeaks: 3,
    stormIslands: 4
})

const DASHBOARD_PROTECTION_LOCATION_LABELS = Object.freeze({
    main: 'Main Castle',
    outpost1: 'Outpost 1',
    outpost2: 'Outpost 2',
    outpost3: 'Outpost 3',
    ice: 'Ice Castle',
    desert: 'Desert Castle',
    fire: 'Fire Castle',
    storm: 'Storm Castle',
    capital: 'Capital',
    metro: 'Metro'
})

const normalizeDashboardCoordPair = (x, y) => {
    const normalizedX = normalizeWholeNumber(x)
    const normalizedY = normalizeWholeNumber(y)
    return normalizedX !== undefined && normalizedY !== undefined ? `${normalizedX}:${normalizedY}` : ''
}

const normalizeDashboardCoordText = value => {
    const match = String(value ?? '').trim().match(/^(-?\d+)\s*:\s*(-?\d+)$/)
    if (!match)
        return ''

    return normalizeDashboardCoordPair(match[1], match[2])
}

const isGenericProtectionSourceLabel = value => {
    const normalized = String(value ?? '').trim().toLowerCase()
    return !normalized || normalized === 'unknown' || normalized === 'unknown castle' || /\bunknown\b/.test(normalized)
}

const buildUniqueDashboardIndex = (map, key, value) => {
    if (!key || !value)
        return

    if (map.has(key) && map.get(key) !== value) {
        map.set(key, null)
        return
    }

    map.set(key, value)
}

const resolveDashboardProtectionLocationKeyForCastle = castle => {
    const kingdomID = normalizeWholeNumber(castle?.kingdomID)
    const areaType = normalizeWholeNumber(castle?.areaType)

    if (areaType === DASHBOARD_AREA_TYPE.capital)
        return 'capital'
    if (kingdomID === DASHBOARD_KINGDOM_ID.everWinterGlacier)
        return 'ice'
    if (kingdomID === DASHBOARD_KINGDOM_ID.burningSands)
        return 'desert'
    if (kingdomID === DASHBOARD_KINGDOM_ID.firePeaks)
        return 'fire'
    if (kingdomID === DASHBOARD_KINGDOM_ID.stormIslands)
        return 'storm'
    if (areaType === DASHBOARD_AREA_TYPE.externalKingdom || areaType === DASHBOARD_AREA_TYPE.metropolis)
        return 'metro'
    if (areaType === DASHBOARD_AREA_TYPE.mainCastle)
        return 'main'

    return ''
}

const buildDashboardTroopCastleSourceIndex = rawCastles => {
    const castles = (Array.isArray(rawCastles) ? rawCastles : [])
        .map(normalizeDashboardTroopCastle)
        .filter(Boolean)
    const byArea = new Map()
    const byCoords = new Map()
    const byLocation = new Map()
    const byLocationOnly = new Map()
    const outpostsByKingdom = new Map()

    castles.forEach(castle => {
        if (castle.kingdomID !== undefined && castle.areaID !== undefined)
            byArea.set(`${castle.kingdomID}:${castle.areaID}`, castle)

        const coordKey = normalizeDashboardCoordPair(castle.x, castle.y)
        if (castle.kingdomID !== undefined && coordKey)
            byCoords.set(`${castle.kingdomID}:${coordKey}`, castle)
        if (coordKey)
            buildUniqueDashboardIndex(byCoords, coordKey, castle)

        if (castle.areaType === DASHBOARD_AREA_TYPE.outpost) {
            const kingdomKey = String(castle.kingdomID ?? '')
            outpostsByKingdom.set(kingdomKey, [...(outpostsByKingdom.get(kingdomKey) ?? []), castle])
            return
        }

        const locationKey = resolveDashboardProtectionLocationKeyForCastle(castle)
        if (locationKey) {
            if (castle.kingdomID !== undefined)
                byLocation.set(`${castle.kingdomID}:${locationKey}`, castle)
            buildUniqueDashboardIndex(byLocationOnly, locationKey, castle)
        }
    })

    for (const [kingdomKey, outposts] of outpostsByKingdom.entries()) {
        outposts
            .sort((left, right) =>
                Number(left.areaID ?? Number.MAX_SAFE_INTEGER) - Number(right.areaID ?? Number.MAX_SAFE_INTEGER) ||
                Number(left.x ?? Number.MAX_SAFE_INTEGER) - Number(right.x ?? Number.MAX_SAFE_INTEGER) ||
                Number(left.y ?? Number.MAX_SAFE_INTEGER) - Number(right.y ?? Number.MAX_SAFE_INTEGER))
            .slice(0, 3)
            .forEach((castle, index) => {
                const locationKey = `outpost${index + 1}`
                if (kingdomKey)
                    byLocation.set(`${kingdomKey}:${locationKey}`, castle)
                buildUniqueDashboardIndex(byLocationOnly, locationKey, castle)
            })
    }

    return { byArea, byCoords, byLocation, byLocationOnly }
}

const findDashboardProtectionSourceCastle = (movement, sourceIndex) => {
    const kingdomID = normalizeWholeNumber(movement?.kingdomID)
    const sourceAreaID = normalizeWholeNumber(movement?.sourceAreaID)
    const sourceCoords = normalizeDashboardCoordText(movement?.sourceCoords)
    const locationKey = String(movement?.locationKey ?? '').trim()

    if (kingdomID !== undefined && sourceAreaID !== undefined && sourceIndex.byArea.has(`${kingdomID}:${sourceAreaID}`))
        return sourceIndex.byArea.get(`${kingdomID}:${sourceAreaID}`)

    if (kingdomID !== undefined && sourceCoords && sourceIndex.byCoords.has(`${kingdomID}:${sourceCoords}`))
        return sourceIndex.byCoords.get(`${kingdomID}:${sourceCoords}`)

    if (sourceCoords && sourceIndex.byCoords.has(sourceCoords))
        return sourceIndex.byCoords.get(sourceCoords)

    if (kingdomID !== undefined && locationKey && sourceIndex.byLocation.has(`${kingdomID}:${locationKey}`))
        return sourceIndex.byLocation.get(`${kingdomID}:${locationKey}`)

    if (locationKey && sourceIndex.byLocationOnly.has(locationKey))
        return sourceIndex.byLocationOnly.get(locationKey)

    return undefined
}

const buildProtectionSourceFallbackLabel = movement => {
    const locationKey = String(movement?.locationKey ?? '').trim()
    const locationLabel = DASHBOARD_PROTECTION_LOCATION_LABELS[locationKey] ?? locationKey
    const sourceCoords = normalizeDashboardCoordText(movement?.sourceCoords)
    const sourceAreaID = normalizeWholeNumber(movement?.sourceAreaID)
    const kingdomID = normalizeWholeNumber(movement?.kingdomID)

    if (locationLabel && sourceCoords)
        return `${locationLabel} ${sourceCoords}`
    if (sourceCoords)
        return sourceCoords
    if (locationLabel && sourceAreaID !== undefined)
        return `${locationLabel} AID ${sourceAreaID}`
    if (sourceAreaID !== undefined && kingdomID !== undefined)
        return `KID ${kingdomID} AID ${sourceAreaID}`
    if (sourceAreaID !== undefined)
        return `AID ${sourceAreaID}`
    if (locationLabel)
        return locationLabel

    return 'Unavailable'
}

const resolveDashboardProtectionSourceLabel = (movement, sourceIndex) => {
    const matchedCastle = findDashboardProtectionSourceCastle(movement, sourceIndex)
    const matchedCastleLabel = String(matchedCastle?.castleLabel ?? '').trim()
    if (matchedCastleLabel && !isGenericProtectionSourceLabel(matchedCastleLabel))
        return matchedCastleLabel

    const rawSourceCastleLabel = String(movement?.sourceCastleLabel ?? '').trim()
    if (rawSourceCastleLabel && !isGenericProtectionSourceLabel(rawSourceCastleLabel))
        return rawSourceCastleLabel

    return buildProtectionSourceFallbackLabel(movement)
}

export const resolveDashboardTroopStock = status => {
    const rawStock = status?.troopStock && typeof status.troopStock === 'object' ? status.troopStock : {}
    const castles = (Array.isArray(rawStock?.castles) ? rawStock.castles : [])
        .map(normalizeDashboardTroopCastle)
        .filter(Boolean)
        .sort((left, right) =>
            Number(right.total) - Number(left.total) ||
            String(left.castleLabel).localeCompare(String(right.castleLabel)))

    const summedTotals = castles.reduce((accumulator, castle) => {
        accumulator.total += castle.total
        accumulator.attack += castle.attack
        accumulator.defense += castle.defense
        accumulator.melee += castle.melee
        accumulator.ranged += castle.ranged
        accumulator.stronghold += castle.stronghold
        accumulator.hospital += castle.hospital
        accumulator.traveling += castle.traveling
        return accumulator
    }, {
        total: 0,
        attack: 0,
        defense: 0,
        melee: 0,
        ranged: 0,
        stronghold: 0,
        hospital: 0,
        traveling: 0
    })

    return {
        updatedAt: normalizeNonNegativeWholeNumber(rawStock?.updatedAt),
        total: normalizeNonNegativeWholeNumber(rawStock?.total) ?? summedTotals.total,
        attack: normalizeNonNegativeWholeNumber(rawStock?.attack) ?? summedTotals.attack,
        defense: normalizeNonNegativeWholeNumber(rawStock?.defense) ?? summedTotals.defense,
        melee: normalizeNonNegativeWholeNumber(rawStock?.melee) ?? summedTotals.melee,
        ranged: normalizeNonNegativeWholeNumber(rawStock?.ranged) ?? summedTotals.ranged,
        stronghold: normalizeNonNegativeWholeNumber(rawStock?.stronghold) ?? summedTotals.stronghold,
        hospital: normalizeNonNegativeWholeNumber(rawStock?.hospital) ?? summedTotals.hospital,
        traveling: normalizeNonNegativeWholeNumber(rawStock?.traveling) ?? summedTotals.traveling,
        castles
    }
}

const normalizeDashboardProtectionMovement = (movement, now, sourceIndex) => {
    const movementId = normalizeWholeNumber(movement?.movementId)
    const kingdomID = normalizeWholeNumber(movement?.kingdomID)
    const sourceAreaID = normalizeWholeNumber(movement?.sourceAreaID)
    const sourceCoords = String(movement?.sourceCoords ?? '').trim()
    const locationKey = String(movement?.locationKey ?? '').trim()
    const troopCount = normalizeNonNegativeWholeNumber(movement?.troopCount) ?? 0
    const serverRemainingSeconds = normalizeNonNegativeWholeNumber(movement?.serverRemainingSeconds)
    const serverElapsedSeconds = normalizeNonNegativeWholeNumber(movement?.serverElapsedSeconds)
    const serverTotalSeconds = normalizeNonNegativeWholeNumber(movement?.serverTotalSeconds)
    const serverReportedAt = normalizeNonNegativeWholeNumber(movement?.serverReportedAt)
    const stationDurationMs = normalizeNonNegativeWholeNumber(movement?.stationDurationMs)
    const stationEndsAt = normalizeNonNegativeWholeNumber(movement?.stationEndsAt)
    const directTravelArrivalAt = normalizeNonNegativeWholeNumber(movement?.travelArrivalAt) ??
        normalizeNonNegativeWholeNumber(resolveMovementArrivalTime({
            deltaTime: movement?.deltaTime,
            arrivalTime: movement?.arrivalTime,
            endTime: movement?.endTime,
            serverRemainingSeconds,
            serverReportedAt
        }, now))
    const inferredStationStartedAt =
        directTravelArrivalAt === undefined &&
            stationEndsAt !== undefined &&
            stationDurationMs !== undefined ?
            Math.max(0, stationEndsAt - stationDurationMs) :
            undefined
    const travelArrivalAt = directTravelArrivalAt ?? inferredStationStartedAt
    const computedStatus =
        travelArrivalAt !== undefined && travelArrivalAt > now ? 'travelling' :
            stationEndsAt !== undefined && stationEndsAt > now ? 'stationed' :
                'returning'

    return {
        source: String(movement?.source ?? '').trim(),
        movementId,
        kingdomID,
        sourceAreaID,
        movementKey: String(movement?.movementKey ?? '').trim(),
        estimated: Boolean(movement?.estimated),
        sourceCastleLabel: resolveDashboardProtectionSourceLabel({
            ...movement,
            kingdomID,
            sourceAreaID,
            sourceCoords,
            locationKey
        }, sourceIndex),
        sourceCoords,
        targetPlayerName: String(movement?.targetPlayerName ?? '').trim(),
        targetLabel: String(movement?.targetLabel ?? movement?.targetCoords ?? '').trim() || 'Unknown',
        targetCoords: String(movement?.targetCoords ?? '').trim(),
        locationKey,
        status: String(movement?.status ?? computedStatus).trim() || computedStatus,
        troopCount,
        movementNumber: normalizePositiveWholeNumber(movement?.movementNumber) ?? 1,
        movementCount: normalizePositiveWholeNumber(movement?.movementCount) ?? 1,
        createdAt: normalizeNonNegativeWholeNumber(movement?.createdAt),
        updatedAt: normalizeNonNegativeWholeNumber(movement?.updatedAt),
        lastSeenAt: normalizeNonNegativeWholeNumber(movement?.lastSeenAt),
        travelStartedAt: normalizeNonNegativeWholeNumber(movement?.travelStartedAt),
        travelArrivalAt,
        travelDurationMs: normalizeNonNegativeWholeNumber(movement?.travelDurationMs),
        travelRemainingMs: travelArrivalAt !== undefined ? Math.max(0, travelArrivalAt - now) : undefined,
        stationDurationMs,
        stationEndsAt,
        stationRemainingMs: stationEndsAt !== undefined ? Math.max(0, stationEndsAt - now) : undefined,
        serverRemainingSeconds,
        serverElapsedSeconds,
        serverTotalSeconds,
        serverReportedAt,
        cancelable: Boolean(movement?.cancelable),
        recallRequestedAt: normalizeNonNegativeWholeNumber(movement?.recallRequestedAt)
    }
}

export const resolveDashboardTroopProtection = (status, now = Date.now()) => {
    const rawProtection = status?.troopProtection && typeof status.troopProtection === 'object' ?
        status.troopProtection :
        {}
    const rawStock = status?.troopStock && typeof status.troopStock === 'object' ? status.troopStock : {}
    const sourceIndex = buildDashboardTroopCastleSourceIndex(rawStock?.castles)
    const movements = (Array.isArray(rawProtection?.movements) ? rawProtection.movements : [])
        .map(movement => normalizeDashboardProtectionMovement(movement, now, sourceIndex))
        .filter(movement => movement.troopCount > 0 || movement.movementId !== undefined)
        .sort((left, right) =>
            Number(left.travelArrivalAt ?? Number.MAX_SAFE_INTEGER) - Number(right.travelArrivalAt ?? Number.MAX_SAFE_INTEGER) ||
            Number(left.stationEndsAt ?? Number.MAX_SAFE_INTEGER) - Number(right.stationEndsAt ?? Number.MAX_SAFE_INTEGER) ||
            Number(left.movementId ?? Number.MAX_SAFE_INTEGER) - Number(right.movementId ?? Number.MAX_SAFE_INTEGER))

    return {
        updatedAt: normalizeNonNegativeWholeNumber(rawProtection?.updatedAt),
        totalTroops: movements.reduce((sum, movement) => sum + movement.troopCount, 0),
        movements
    }
}

const isDefenseProtectionMovement = movement =>
    ['flee', 'stock'].includes(String(movement?.source ?? '').trim().toLowerCase()) ||
    /^estimated:|^stock:/i.test(String(movement?.movementKey ?? '').trim())

const getProtectionMovementCardTone = movement =>
    isDefenseProtectionMovement(movement) ?
        {
            backgroundColor: 'rgba(127,29,29,0.22)',
            borderColor: 'rgba(248,113,113,0.46)',
            accentColor: '#ef4444',
            headingColor: '#fee2e2',
            labelColor: '#fecaca',
            valueColor: '#fee2e2',
            troopColor: '#fca5a5'
        } :
        {
            backgroundColor: 'rgba(59,130,246,0.08)',
            borderColor: 'rgba(147,197,253,0.18)',
            accentColor: 'rgba(147,197,253,0.32)',
            headingColor: 'text.primary',
            labelColor: '#dbeafe',
            valueColor: 'text.secondary',
            troopColor: 'text.secondary'
        }

const formatDashboardClockTime = timestamp => {
    const parsed = Number(timestamp)
    if (!Number.isFinite(parsed) || parsed <= 0)
        return 'Unavailable'

    return new Date(parsed).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    })
}

const formatDashboardDateTime = timestamp => {
    const parsed = Number(timestamp)
    if (!Number.isFinite(parsed) || parsed <= 0)
        return 'Unavailable'

    return new Date(parsed).toLocaleString([], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

const getNextDashboardUtcDayResetAt = () => {
    const nextDay = new Date()
    nextDay.setUTCHours(24, 0, 0, 250)
    return nextDay.getTime()
}

const resolveDashboardDailyAttackLimitEntries = status =>
    Object.entries(
        status?.dailyAttackLimits && typeof status.dailyAttackLimits === 'object' ?
            status.dailyAttackLimits :
            {}
    )
        .map(([moduleKey, entry]) => {
            const rawAttackCount = Number(entry?.attackCount ?? 0)
            const rawTrackedAttackCount = Number(entry?.trackedAttackCount)
            const attackCount = Number.isFinite(rawAttackCount) && rawAttackCount > 0 ? Math.floor(rawAttackCount) : 0
            const serverLimit = Math.max(1, Math.floor(normalizePositiveWholeNumber(entry?.serverLimit) ?? normalizePositiveWholeNumber(entry?.effectiveThreshold) ?? 3500))
            const effectiveThreshold = Math.max(1, Math.floor(normalizePositiveWholeNumber(entry?.effectiveThreshold) ?? normalizePositiveWholeNumber(entry?.configuredThreshold) ?? serverLimit))

            return {
                moduleKey,
                label: String(entry?.label ?? moduleKey),
                attackCount,
                countSource: String(entry?.countSource ?? '').trim(),
                trackedAttackCount:
                    entry?.trackedAttackCount === undefined || entry?.trackedAttackCount === null ?
                        undefined :
                        (Number.isFinite(rawTrackedAttackCount) && rawTrackedAttackCount >= 0 ? Math.floor(rawTrackedAttackCount) : 0),
                serverLimit,
                effectiveThreshold,
                vipCommanderUsageCount: normalizeWholeNumber(entry?.vipCommanderUsageCount),
                vipCommanderUsageLimit: normalizePositiveWholeNumber(entry?.vipCommanderUsageLimit),
                vipCommanderUsageRemaining: normalizeWholeNumber(entry?.vipCommanderUsageRemaining),
                vipCommanderLimitReached: Boolean(entry?.vipCommanderLimitReached),
                resetAt: Number(entry?.resetAt ?? NaN),
                reached: Boolean(entry?.reached),
                updatedAt: Number(entry?.updatedAt ?? NaN)
            }
        })
        .sort((left, right) => String(left.label ?? '').localeCompare(String(right.label ?? '')))

const resolveDashboardDailyAttackTotalEntry = status => {
    const directEntry =
        status?.dailyAttackTotal && typeof status.dailyAttackTotal === 'object' ?
            status.dailyAttackTotal :
            null

    if (directEntry) {
        const attackCount = Number(directEntry?.attackCount ?? 0)
        const serverLimit = Math.max(1, Math.floor(normalizePositiveWholeNumber(directEntry?.serverLimit) ?? 3500))

        return {
            label: String(directEntry?.label ?? 'All attacks'),
            attackCount: Number.isFinite(attackCount) && attackCount >= 0 ? Math.floor(attackCount) : 0,
            serverLimit,
            countSource: String(directEntry?.countSource ?? '').trim(),
            resetAt: Number(directEntry?.resetAt ?? NaN),
            reached: Boolean(directEntry?.reached),
            updatedAt: Number(directEntry?.updatedAt ?? NaN)
        }
    }

    const entries = resolveDashboardDailyAttackLimitEntries(status)
    if (entries.length <= 0)
        return {
            label: 'All attacks',
            attackCount: 0,
            serverLimit: 3500,
            countSource: 'waiting_for_sync',
            resetAt: getNextDashboardUtcDayResetAt(),
            reached: false,
            updatedAt: NaN
        }

    return {
        label: 'All attacks',
        attackCount: entries.reduce((sum, entry) => sum + Math.max(0, Number(entry?.attackCount ?? 0)), 0),
        serverLimit: Math.max(1, ...entries.map(entry => Math.max(1, Number(entry?.serverLimit ?? 3500)))),
        countSource: entries.some(entry => entry?.countSource === 'bot_backup') ? 'bot_backup' :
            entries.every(entry => entry?.countSource === 'gge_server') ? 'gge_server' :
                'server_log',
        resetAt: Number(entries.find(entry => Number.isFinite(Number(entry?.resetAt)))?.resetAt ?? NaN),
        reached: entries.some(entry => entry?.reached),
        updatedAt: Math.max(...entries.map(entry => Number(entry?.updatedAt ?? 0)))
    }
}

export const resolveDashboardStandbyState = (status, now = Date.now()) => {
    const retryAt = normalizeFiniteNumber(status?.standbyRetryAt)
    const attempt = normalizePositiveWholeNumber(status?.standbyAttempt)
    const maxAttempts = normalizePositiveWholeNumber(status?.standbyMaxAttempts)

    return {
        active: Boolean(status?.standbyActive),
        reason: String(status?.standbyReason ?? '').trim(),
        retryAt,
        remainingMs: retryAt !== undefined ? Math.max(0, retryAt - now) : undefined,
        attempt,
        maxAttempts
    }
}

const formatDuration = durationMs => {
    if (!Number.isFinite(durationMs) || durationMs <= 0)
        return '00:00:00'

    const totalSeconds = Math.floor(durationMs / 1000)
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
    const seconds = String(totalSeconds % 60).padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
}

const formatDashboardAttackTimeLeft = (arrivalTime, now = Date.now()) => {
    const numericArrivalTime = Number(arrivalTime)
    if (!Number.isFinite(numericArrivalTime) || numericArrivalTime <= 0)
        return 'Unknown'

    const remainingMs = numericArrivalTime - Number(now)
    if (remainingMs <= 0)
        return 'Now'

    return formatDuration(remainingMs)
}

const buildDashboardAttackAlertKey = attack => {
    const movementId = Number(attack?.movementId)
    if (Number.isFinite(movementId))
        return `movement:${movementId}`

    const storedId = String(attack?.id ?? '').trim()
    if (storedId)
        return storedId

    const messageID = Number(attack?.messageID)
    if (Number.isFinite(messageID))
        return `message:${messageID}`

    return [
        String(attack?.attackerId ?? attack?.sourceOwnerId ?? attack?.attackerName ?? 'Unknown').trim().toLowerCase(),
        String(attack?.sourceCoords ?? '').trim(),
        String(attack?.targetCoords ?? '').trim(),
        String(attack?.arrivalTime ?? attack?.lastArrivalTime ?? '').trim()
    ].join('|')
}

export const resolveDashboardAttackAlertEntries = (status, now = Date.now()) => {
    const entriesByKey = new Map()
    const addEntry = (attack, active, index) => {
        if (!attack)
            return

        const key = buildDashboardAttackAlertKey(attack)
        if (entriesByKey.has(key))
            return

        const arrivalTime = resolveAttackEntryArrivalTime(attack, now) ?? Number(attack?.lastArrivalTime ?? NaN)
        const lastSeenAt = Number(attack?.lastSeenAt ?? status?.lastIncomingAttackAt ?? NaN)
        const attackTroopCount = normalizeNonNegativeWholeNumber(attack?.attackTroopCount ?? attack?.visibleArmySize)

        entriesByKey.set(key, {
            id: key || `attack:${index}`,
            active,
            attackerName: String(attack?.attackerName ?? 'Unknown'),
            targetCastleName: String(attack?.targetCastleName ?? attack?.castleName ?? '').trim(),
            targetOwnerName: String(attack?.targetOwnerName ?? '').trim(),
            targetCoords: String(attack?.targetCoords ?? '--'),
            sourceCoords: String(attack?.sourceCoords ?? '--'),
            arrivalTime: Number.isFinite(arrivalTime) ? arrivalTime : undefined,
            lastSeenAt: Number.isFinite(lastSeenAt) ? lastSeenAt : undefined,
            attackTroopCount
        })
    }

    ;(Array.isArray(status?.incomingAttacks) ? status.incomingAttacks : [])
        .forEach((attack, index) => addEntry(attack, true, index))
    ;(Array.isArray(status?.recentIncomingAttackers) ? status.recentIncomingAttackers : [])
        .forEach((attack, index) => addEntry(attack, Boolean(attack?.active), index))

    return [...entriesByKey.values()]
        .sort((left, right) =>
            Number(Boolean(right.active)) - Number(Boolean(left.active)) ||
            Number(left.arrivalTime ?? Number.MAX_SAFE_INTEGER) - Number(right.arrivalTime ?? Number.MAX_SAFE_INTEGER) ||
            Number(right.lastSeenAt ?? 0) - Number(left.lastSeenAt ?? 0) ||
        String(left.attackerName ?? '').localeCompare(String(right.attackerName ?? '')))
}

const resolveDashboardAttackTargetLabel = entry =>
    String(
        entry?.targetCastleName ||
        entry?.castleName ||
        entry?.targetCoords ||
        '--'
    ).trim() || '--'

const getApprovalSummary = (user, now = Date.now()) => {
    if (!user?.approved)
        return { label: 'Approval required' }

    const approvedUntil = Number(user?.approvedUntil)
    if (!Number.isFinite(approvedUntil) || approvedUntil <= 0)
        return { label: 'Approved' }

    const remainingMs = approvedUntil - now
    if (remainingMs <= 0)
        return { label: 'Approval expired' }

    const totalMinutes = Math.floor(remainingMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)

    if (days > 0)
        return { label: `Approved ${days}d ${hours}h` }

    return { label: `Approved ${Math.max(1, totalMinutes)}m` }
}

export default function DashboardBotCard({
    user,
    status,
    plugins,
    ws,
    onOpenSettings,
    onOpenLogs,
    onOpenChat,
    onDelete,
    now,
    layout = 'panel',
    appLanguage = 'en'
}) {
    const bthMode = isBthAccount(user)
    const [active, setActive] = React.useState(Boolean(user?.state))
    const [expanded, setExpanded] = React.useState(layout === 'panel')
    const [attackAlertExpanded, setAttackAlertExpanded] = React.useState(true)
    const [individualProtectionExpanded, setIndividualProtectionExpanded] = React.useState(false)
    const [manualCastleDefenseAction, setManualCastleDefenseAction] = React.useState('')
    const manualCastleDefenseTimerRef = React.useRef()
    const t = useAutoTranslate(appLanguage, [
        'Stopped',
        'Running',
        'Standby',
        'Resume Bot',
        'Under attack',
        'Attack warning',
        'Attacked by',
        'Recent attackers',
        'Target',
        'Arrival',
        'Impact in',
        'Last seen',
        'Show attack details',
        'Hide attack details',
        'Configure',
        'Live Ops',
        'Delete',
        'Stop',
        'Start',
        'More details',
        'Less details',
        'Owner',
        'UUID',
        'Alliance',
        'Power',
        'Honor',
        'Approval',
        'Uptime',
        'Alliance Chat',
        'Unknown',
        'Unavailable',
        'No active modules',
        'Modules',
        'Alliance RIFT Portal',
        'Grand Tournament',
        'Alliance ranking',
        'Alliance contribution',
        'Personal contribution',
        'Progress against first place',
        'Alliance name',
        'Total points',
        'Waiting for tournament ranking data',
        'Tournament ranking is temporarily unavailable',
        'Copper',
        'Glass',
        'Bronze',
        'Silver',
        'Gold',
        'Diamond',
        'Unranked',
        'RIFT boss',
        'Level',
        'Portal active',
        'Boss level',
        'Portal health',
        'RIFT points',
        'Attacks sent',
        'Left wall',
        'Middle wall',
        'Right wall',
        'RIFT alliance ranking',
        'Waiting for RIFT ranking data',
        'RIFT ranking is temporarily unavailable',
        'RIFT ranking appears during alliance quests',
        'Approval required',
        'Approved',
        'Approval expired',
        'Player rankings',
        'Kingdom\'s League',
        'Division ranking',
        'Coins',
        'Rubies',
        'Rubies earned',
        'Coins earned',
        'Aquamarine earned',
        'Battle rewards',
        'Reports read',
        'No reward reports yet',
        'Last report',
        'Khan horns',
        'Khan active',
        'Khan attacks (30m)',
        'Last Khan seen',
        'Khan wall tools',
        'Tools restored',
        'Refill counter',
        'Complete',
        'Missing',
        'Defense monitoring',
        'Last defense losses',
        'Defense losses this session',
        'Wounded in last defense',
        'Last defense',
        'Victory',
        'Defeat',
        'No defense reports yet',
        'Soldiers',
        'Aquamarine',
        'Spent',
        'Internal alliance contest',
        'Points',
        'Contest points',
        'On hand',
        'Your rank',
        'Alliance rank',
        'players',
        'ATTENTION',
        'Resources stable',
        'Resource warnings',
        'Food',
        'Honey',
        'Mead',
        'Current stock',
        'Troops start dying in',
        'Depletes in',
        'Rate',
        'Food | Honey | Mead',
        'more',
        'Troop stock',
        'Troops',
        'Soldiers',
        'Castles',
        'Attack',
        'Defense',
        'Melee',
        'Ranged',
        'Stronghold',
        'Hospital',
        'Traveling',
        'No troop stock data yet',
        'Protection movements',
        'Protected troops',
        'No troops are under protection right now',
        'On the road',
        'Stationed',
        'Returning',
        'Travel left',
        'Travel time',
        'Station left',
        'Station time',
        'Departed',
        'Arrival',
        'Station ends',
        'Last update',
        'GGE timer',
        'Source',
        'Receiver',
        'Destination',
        'Movement',
        'Daily attacks',
        'All attacks',
        'No attack counters yet',
        'Attack counters appear after the first successful hit.',
        'Attack limit',
        'Remaining today',
        'Count source',
        'Server log + live hits',
        'Bot backup + live hits',
        'GGE server',
        'Waiting for GGE sync',
        'Reset at',
        'Last sync',
        'Threshold reached today',
        'Storm VIP left today',
        'Storm VIP used today',
        'Storm VIP limit reached',
        "Kingdom's League | Season ranking",
        'Season rank',
        'Division rank',
        'Berri World ranking',
        'Battle for Berimond',
        'Bravery ranking',
        'Battle for Berimond | Bravery ranking',
        'Bravery points',
        'Bracket',
        'No Berri World ranking yet',
        'No Battle for Berimond players yet',
        'Top players',
        'Individual top',
        'Current event rankings',
        'Players around your rank',
        "No Kingdom's League top yet",
        "No Kingdom's League players near your rank yet",
        'Division',
        'Rank',
        'Name',
        'Points',
        'Gold',
        'Silver',
        'Bronze',
        'Recall stationed troops',
        'Send troops to protection',
        'Castle Defense must be enabled and the bot must be running.',
        'Recall all Castle Defense troops from every kingdom?',
        'Send troops from every enabled Castle Defense location using the saved settings?',
        'Send from a specific castle or outpost',
        'Hide individual protection actions',
        'Show individual protection actions',
        'Send this location to protection',
        'Enable troop sending for this location in Castle Defense settings.',
        'Send troops from this Castle Defense location using its saved settings?',
        'Main Castle',
        'Outpost 1',
        'Outpost 2',
        'Outpost 3',
        'Ice',
        'Desert',
        'Fire',
        'Storm',
        'Capital',
        'Metro',
        'Working...'
    ])

    React.useEffect(() => setActive(Boolean(user?.state)), [user?.state])
    React.useEffect(() => () => clearTimeout(manualCastleDefenseTimerRef.current), [])
    React.useEffect(() => {
        if (layout === 'list')
            setExpanded(false)
    }, [layout])

    const effectiveNow = React.useMemo(() => {
        const numericNow = Number(now)
        return Number.isFinite(numericNow) ? numericNow : Date.now()
    }, [now])

    const activePluginNames = getDashboardActivePluginNames(user?.plugins, plugins, user?.botSettings, bthMode)
    const underAttack = Boolean(status?.isUnderAttack)
    const attackAlertEntries = React.useMemo(
        () => resolveDashboardAttackAlertEntries(status, effectiveNow),
        [effectiveNow, status]
    )
    const visibleAttackAlertEntries = attackAlertEntries.slice(0, 5)
    const attackAlertNames = visibleAttackAlertEntries
        .map(entry => entry.attackerName)
        .filter(Boolean)
        .filter((name, index, names) => names.indexOf(name) === index)
    const attackAlertSummary = attackAlertNames.length > 0 ?
        attackAlertNames.join(', ') :
        t('Unknown')
    const approvalSummary = getApprovalSummary(user, effectiveNow)
    const allianceName = String(status?.allianceName ?? '').trim()
    const ownerLabel = String(user?.ownerUsername ?? '').trim() || t('Unknown')
    const uptimeLabel = formatDuration(resolveRuntimeUptimeMs(status, effectiveNow))
    const incomingKhanTauntCount = resolveIncomingKhanTauntCount(status)
    const recentKhanAttackCount = resolveRecentKhanAttackCount(status)
    const lastKhanAttackAt = Number(status?.lastKhanAttackAt ?? NaN)
    const khanToolsEquippedCount = normalizeNonNegativeWholeNumber(status?.khanDefenceToolsEquippedCount)
    const khanToolsTargetCount = normalizeNonNegativeWholeNumber(status?.khanDefenceToolsTargetCount)
    const khanToolsMissingCount = normalizeNonNegativeWholeNumber(status?.khanDefenceToolsMissingCount) ?? 0
    const khanToolsPendingHits = normalizeNonNegativeWholeNumber(status?.khanDefenceToolsPendingHits) ?? 0
    const khanToolsRefillThreshold = normalizePositiveWholeNumber(status?.khanDefenceToolsRefillThreshold)
    const khanToolsLastRefilledAt = Number(status?.khanDefenceToolsLastRefilledAt ?? NaN)
    const khanToolsLastRefilledCount = normalizeNonNegativeWholeNumber(status?.khanDefenceToolsLastRefilledCount) ?? 0
    const hasKhanToolStatus = khanToolsEquippedCount !== undefined && khanToolsTargetCount !== undefined
    const defensiveBattlesRead = normalizeNonNegativeWholeNumber(status?.defensiveBattlesRead) ?? 0
    const defensiveTroopsLost = normalizeNonNegativeWholeNumber(status?.defensiveTroopsLost) ?? 0
    const lastDefensiveTroopsLost = normalizeNonNegativeWholeNumber(status?.lastDefensiveTroopsLost)
    const lastDefensiveWounded = normalizeNonNegativeWholeNumber(status?.lastDefensiveWounded) ?? 0
    const lastDefenseReportAt = Number(status?.lastDefenseReportAt ?? NaN)
    const lastDefenseAttackerName = String(status?.lastDefenseAttackerName ?? '').trim()
    const powerValue = Number(status?.mightPoints)
    const powerLabel = Number.isFinite(powerValue) && powerValue > 0 ? numberFormatter.format(powerValue) : t('Unavailable')
    const honourValue = Number(status?.honour ?? status?.honor)
    const honourLabel = Number.isFinite(honourValue) && honourValue >= 0 ? numberFormatter.format(honourValue) : t('Unavailable')
    const kingdomLeagueSummary = buildDashboardKingdomLeagueSummary(status, t('Unavailable'))
    const grandTournament = React.useMemo(
        () => resolveDashboardGrandTournament(status, effectiveNow),
        [effectiveNow, status]
    )
    const allianceRift = React.useMemo(
        () => resolveDashboardAllianceRift(status, effectiveNow),
        [effectiveNow, status]
    )
    const kingdomLeagueTopEntries = React.useMemo(() => resolveDashboardKingdomLeagueTopEntries(status), [status])
    const kingdomLeagueLeaderboardSource = String(status?.leagueSeasonLeaderboardSource ?? '').trim()
    const kingdomLeagueLeaderboardUpdatedAt = Number(status?.leagueSeasonLeaderboardUpdatedAt ?? NaN)
    const berriWorldEnabled = Boolean(user?.plugins?.berikingdom?.state)
    const berriWorldSummary = buildDashboardBerriWorldSummary(status, t('Unavailable'))
    const berriWorldTopEntries = React.useMemo(() => resolveDashboardBerriWorldTopEntries(status), [status])
    const berriWorldLeaderboardSource = String(status?.berriWorldLeaderboardSource ?? '').trim()
    const berriWorldLeaderboardUpdatedAt = Number(status?.berriWorldLeaderboardUpdatedAt ?? NaN)
    const currencies = resolveDashboardCurrencies(status)
    const dailyAttackEntries = React.useMemo(() => resolveDashboardDailyAttackLimitEntries(status), [status])
    const dailyAttackSummaryEntry = React.useMemo(() => resolveDashboardDailyAttackTotalEntry(status), [status])
    const stormDailyAttackEntry = React.useMemo(
        () => dailyAttackEntries.find(entry => entry.moduleKey === 'stormAquaForts') ?? null,
        [dailyAttackEntries]
    )
    const standbyState = resolveDashboardStandbyState(status, effectiveNow)
    const isStandby = standbyState.active
    const coinsLabel = formatDashboardWholeNumber(currencies.coins, t('Unavailable'))
    const rubiesLabel = formatDashboardWholeNumber(currencies.rubies, t('Unavailable'))
    const aquamarineLabel = formatDashboardWholeNumber(currencies.aquamarine, t('Unavailable'))
    const earnedRubiesValue = Math.max(0, Math.floor(Number(status?.earnedRubies ?? 0) || 0))
    const earnedCoinsValue = Math.max(0, Math.floor(Number(status?.earnedCoins ?? 0) || 0))
    const earnedAquamarineValue = Math.max(0, Math.floor(Number(status?.earnedAquamarine ?? 0) || 0))
    const battleReportsRead = Math.max(0, Math.floor(Number(status?.battleReportsRead ?? 0) || 0))
    const lastBattleReportAt = Number(status?.lastBattleReportAt ?? NaN)
    const rewardReportStatusLabel =
        battleReportsRead > 0 ?
            `${t('Reports read')}: ${numberFormatter.format(battleReportsRead)}` :
            t('No reward reports yet')
    const leagueSeasonRankLabel = formatDashboardRank(status?.leagueSeasonRank, t('Unavailable'))
    const leagueDivisionRankLabel = formatDashboardRank(status?.leagueDivisionRank, t('Unavailable'))
    const leagueDivisionIdLabel = formatDashboardWholeNumber(status?.leagueDivisionId, t('Unavailable'))
    const leagueDivisionSizeLabel = formatDashboardWholeNumber(status?.leagueDivisionSize, t('Unavailable'))
    const berriWorldRankLabel = formatDashboardRank(status?.berriWorldRank, t('Unavailable'))
    const berriWorldPointsLabel = formatDashboardWholeNumber(status?.berriWorldPoints, t('Unavailable'))
    const berriWorldLootIDLabel = formatDashboardWholeNumber(status?.berriWorldLootID, t('Unavailable'))
    const leaguePlayersHeadingLabel =
        kingdomLeagueLeaderboardSource === 'own_rank_window' ||
        kingdomLeagueLeaderboardSource === 'current_event_division' ||
        kingdomLeagueLeaderboardSource === 'current_event_top' ?
            t('Current event rankings') :
        kingdomLeagueLeaderboardSource === 'individual_top_page' ?
            t('Individual top') :
            t('Top players')
    const leaguePlayersEmptyLabel =
        kingdomLeagueLeaderboardSource === 'own_rank_window' ?
            t("No Kingdom's League players near your rank yet") :
            t("No Kingdom's League top yet")
    const berriWorldPlayersHeadingLabel =
        berriWorldLeaderboardSource === 'own_rank_window' ?
            t('Players around your rank') :
            t('Top players')
    const internalContestRankLabel = formatDashboardRank(currencies.internalContestRank, t('Unavailable'))
    const internalContestPointsLabel = formatDashboardWholeNumber(currencies.internalContestPoints, t('Unavailable'))
    const allianceStormRankLabel = formatDashboardRank(currencies.allianceStormRank, t('Unavailable'))
    const resourceAlert = resolveDashboardResourceAlert(status, effectiveNow)
    const troopStock = React.useMemo(() => resolveDashboardTroopStock(status), [status])
    const troopProtection = React.useMemo(
        () => resolveDashboardTroopProtection(status, effectiveNow),
        [effectiveNow, status]
    )
    const canStart = Boolean(user?.approved)
    const castleDefenseEnabled = Boolean(user?.plugins?.flee?.state)
    const canUseManualCastleDefense = active && !isStandby && castleDefenseEnabled
    const showPrimaryAction = isStandby || active || canStart
    const primaryLabel = isStandby ? t('Resume Bot') : active ? t('Stop') : t('Start')
    const stateLabel = isStandby ? t('Standby') : active ? t('Running') : t('Stopped')
    const resourceAlertDetails = resourceAlert.hasAlert ?
        resourceAlert.details.map((detail, index) => {
            const etaMs = detail.depletionRemainingMs ?? detail.depletionEtaMs
            return {
                key: `${detail.castleLabel || 'castle'}-${detail.resourceLabel || 'resource'}-${index}`,
                castleLabel: String(detail.castleLabel ?? '').trim() || t('Unavailable'),
                resourceLabel: detail?.resourceLabel ? t(detail.resourceLabel) : t('Unavailable'),
                rateLabel: detail?.delta !== undefined ? `${numberFormatter.format(detail.delta)}/h` : t('Unavailable'),
                currentStockLabel: formatDashboardWholeNumber(detail.currentAmount, t('Unavailable')),
                depletionLabel: etaMs !== undefined ? formatDuration(etaMs) : t('Unavailable')
            }
        }) :
        []
    const troopProtectionStatusLabel = movement =>
        movement.status === 'travelling' ? t('On the road') :
            movement.status === 'stationed' ? t('Stationed') :
                t('Returning')
    const troopProtectionStatusColor = movement =>
        movement.status === 'travelling' ? '#93c5fd' :
            movement.status === 'stationed' ? '#86efac' :
                'text.secondary'
    const standbyCaption = isStandby ?
        [
            standbyState.remainingMs !== undefined ? `Retry ${formatDuration(standbyState.remainingMs)}` : '',
            standbyState.attempt !== undefined && standbyState.maxAttempts !== undefined ? `${standbyState.attempt}/${standbyState.maxAttempts}` : ''
        ].filter(Boolean).join(' | ') :
        ''
    const stateChipColor = isStandby ? '#fbbf24' : active ? '#7dd3fc' : 'text.secondary'
    const detailCardSx = {
        p: 1.15,
        borderRadius: 2.2,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
        height: '100%'
    }
    const alertCardSx = {
        ...detailCardSx,
        backgroundColor: resourceAlert.hasAlert ? 'rgba(239,68,68,0.14)' : detailCardSx.backgroundColor,
        border: resourceAlert.hasAlert ? '1px solid rgba(248,113,113,0.45)' : detailCardSx.border
    }
    const attackCardSx = underAttack ? {
        borderColor: 'rgba(248,113,113,0.72)',
        backgroundColor: 'rgba(127,29,29,0.22)',
        boxShadow: '0 0 0 1px rgba(248,113,113,0.16), 0 18px 44px rgba(127,29,29,0.22)'
    } : {}
    const attackAlertPanel = visibleAttackAlertEntries.length > 0 ? (
        <Box
            sx={{
                mt: 1.2,
                p: 1.15,
                borderRadius: 2.2,
                backgroundColor: underAttack ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.035)',
                border: underAttack ? '1px solid rgba(248,113,113,0.42)' : '1px solid rgba(255,255,255,0.07)'
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmberRoundedIcon sx={{ color: underAttack ? '#fca5a5' : '#fbbf24', fontSize: 18 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: underAttack ? '#fecaca' : 'text.secondary', fontWeight: 700 }}>
                        {underAttack ? t('Attack warning') : t('Recent attackers')}
                    </Typography>
                </Box>
                <Tooltip title={attackAlertExpanded ? t('Hide attack details') : t('Show attack details')}>
                    <IconButton
                        size="small"
                        aria-label={attackAlertExpanded ? t('Hide attack details') : t('Show attack details')}
                        aria-expanded={attackAlertExpanded}
                        data-testid="attack-alert-toggle"
                        onClick={() => setAttackAlertExpanded(previous => !previous)}
                        sx={{
                            ml: 'auto',
                            color: underAttack ? '#fecaca' : 'text.secondary',
                            p: 0.35
                        }}
                    >
                        {attackAlertExpanded ?
                            <KeyboardArrowUpRoundedIcon sx={{ fontSize: 20 }} /> :
                            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Tooltip>
            </Stack>
            <Collapse in={attackAlertExpanded} timeout="auto" unmountOnExit>
                <Box data-testid="attack-alert-details">
                    <Typography variant="caption" sx={{ display: 'block', color: underAttack ? '#ffe4e6' : 'text.secondary', overflowWrap: 'anywhere' }}>
                        {`${underAttack ? t('Attacked by') : t('Recent attackers')}: ${attackAlertSummary}`}
                    </Typography>
                    <Stack spacing={0.45} sx={{ mt: 0.85 }}>
                        {visibleAttackAlertEntries.map(entry => (
                            <Typography
                                key={entry.id}
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    color: entry.active ? '#ffe4e6' : 'text.secondary',
                                    overflowWrap: 'anywhere',
                                    lineHeight: 1.35
                                }}
                            >
                                {`${entry.attackerName} -> `}
                                <Box component="span" sx={{ color: entry.active ? '#fde68a' : 'inherit', fontWeight: 700 }}>
                                    {resolveDashboardAttackTargetLabel(entry)}
                                </Box>
                                {' | '}
                                <Box component="span" sx={{ color: entry.active ? '#fecaca' : 'inherit', fontWeight: entry.active ? 700 : 400 }}>
                                    {`${t('Soldiers')}: ${entry.attackTroopCount !== undefined ? numberFormatter.format(entry.attackTroopCount) : t('Unknown')}`}
                                </Box>
                                {' | '}
                                <Box component="span" sx={{ color: entry.active ? '#bbf7d0' : 'inherit', fontWeight: entry.active ? 700 : 400 }}>
                                    {entry.active ?
                                        `${t('Impact in')}: ${formatDashboardAttackTimeLeft(entry.arrivalTime, effectiveNow)}` :
                                        `${t('Last seen')}: ${formatDashboardClockTime(entry.lastSeenAt)}`}
                                </Box>
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    ) : null

    const dailyAttackCount = Number(dailyAttackSummaryEntry?.attackCount ?? 0)
    const dailyAttackServerLimit = Math.max(1, Number(dailyAttackSummaryEntry?.serverLimit ?? 3500))
    const dailyAttackRemaining = Math.max(0, dailyAttackServerLimit - dailyAttackCount)
    const dailyAttackProgress = Math.max(0, Math.min(100, (dailyAttackCount / dailyAttackServerLimit) * 100))
    const stormVipUsageCount = Math.max(0, Number(stormDailyAttackEntry?.vipCommanderUsageCount ?? 0))
    const stormVipUsageLimit = Math.max(0, Number(stormDailyAttackEntry?.vipCommanderUsageLimit ?? 0))
    const stormVipRemaining =
        stormVipUsageLimit > 0 ?
            Math.max(0, Number(stormDailyAttackEntry?.vipCommanderUsageRemaining ?? (stormVipUsageLimit - stormVipUsageCount))) :
            undefined
    const dailyAttackCountSourceLabel =
        dailyAttackSummaryEntry?.countSource === 'gge_server' ?
            t('GGE server') :
            dailyAttackSummaryEntry?.countSource === 'waiting_for_sync' ?
                t('Waiting for GGE sync') :
            dailyAttackSummaryEntry?.countSource === 'bot_backup' ?
                t('Bot backup + live hits') :
                t('Server log + live hits')

    const setBotState = nextState => {
        setActive(nextState)
        user.state = nextState ? 1 : 0
        ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, user]))
    }

    const toggleState = () => {
        setBotState(!active)
    }

    const handleStopBot = () => {
        setBotState(false)
    }

    const handleResumeBot = () => {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.ResumeBot, user]))
    }

    const handlePrimaryAction = () => {
        if (isStandby)
            return handleResumeBot()

        toggleState()
    }

    const sendManualCastleDefenseAction = (action, confirmationText, pendingKey, locationKey) => {
        if (!canUseManualCastleDefense || manualCastleDefenseAction)
            return

        if (!window.confirm(confirmationText))
            return

        setManualCastleDefenseAction(pendingKey)
        ws.send(JSON.stringify([
            ErrorType.Success,
            action,
            locationKey ? { ...user, manualCastleDefenseLocationKey: locationKey } : user
        ]))
        clearTimeout(manualCastleDefenseTimerRef.current)
        manualCastleDefenseTimerRef.current = setTimeout(() => {
            setManualCastleDefenseAction('')
        }, 4000)
    }

    const renderActionIcon = (backgroundColor, icon, onClick, ariaLabel) => (
        <IconButton
            onClick={onClick}
            aria-label={ariaLabel}
            sx={{
                width: 44,
                height: 36,
                borderRadius: 2.2,
                border: backgroundColor === '#ef2f3b' || backgroundColor === '#10b981' || backgroundColor === '#ef4444'
                    ? 'none'
                    : '1px solid rgba(255,255,255,0.12)',
                backgroundColor,
                color: '#fff',
                '&:hover': {
                    backgroundColor
                }
            }}
        >
            {icon}
        </IconButton>
    )

    const quickActions = (
        <Stack direction="row" spacing={0.9} alignItems="center">
            {showPrimaryAction && renderActionIcon(
                isStandby ? '#10b981' : active ? '#ef4444' : '#10b981',
                isStandby ? <PlayArrowRoundedIcon sx={{ fontSize: 22 }} /> : active ? <StopRoundedIcon sx={{ fontSize: 22 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />,
                handlePrimaryAction,
                primaryLabel
            )}
            {isStandby && renderActionIcon(
                '#ef4444',
                <StopRoundedIcon sx={{ fontSize: 22 }} />,
                handleStopBot,
                t('Stop')
            )}
            {renderActionIcon('rgba(255,255,255,0.04)', <SettingsRoundedIcon sx={{ fontSize: 20 }} />, onOpenSettings, t('Configure'))}
            {renderActionIcon('rgba(255,255,255,0.04)', <MonitorHeartRoundedIcon sx={{ fontSize: 20 }} />, onOpenLogs, t('Live Ops'))}
            {renderActionIcon('#ef2f3b', <DeleteRoundedIcon sx={{ fontSize: 20 }} />, () => onDelete?.(user), t('Delete'))}
        </Stack>
    )

    const castleDefenseActions = (
        <Box
            sx={{
                p: 0.85,
                border: '1px solid rgba(245,158,11,0.48)',
                backgroundColor: 'rgba(245,158,11,0.055)'
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.9}>
                <Tooltip
                    title={canUseManualCastleDefense ? t('Recall stationed troops') : t('Castle Defense must be enabled and the bot must be running.')}
                >
                    <span style={{ display: 'flex', flex: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<KeyboardReturnRoundedIcon />}
                            disabled={!canUseManualCastleDefense || Boolean(manualCastleDefenseAction)}
                            onClick={() => sendManualCastleDefenseAction(
                                ActionType.ManualCastleDefenseRecall,
                                t('Recall all Castle Defense troops from every kingdom?'),
                                'recall'
                            )}
                            sx={{
                                minHeight: 38,
                                borderRadius: 1.5,
                                color: '#fbbf24',
                                borderColor: 'rgba(251,191,36,0.62)',
                                '&:hover': {
                                    borderColor: '#fbbf24',
                                    backgroundColor: 'rgba(251,191,36,0.09)'
                                }
                            }}
                        >
                            {manualCastleDefenseAction === 'recall' ? t('Working...') : t('Recall stationed troops')}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip
                    title={canUseManualCastleDefense ? t('Send troops to protection') : t('Castle Defense must be enabled and the bot must be running.')}
                >
                    <span style={{ display: 'flex', flex: 1 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<ShieldRoundedIcon />}
                            disabled={!canUseManualCastleDefense || Boolean(manualCastleDefenseAction)}
                            onClick={() => sendManualCastleDefenseAction(
                                ActionType.ManualCastleDefenseSend,
                                t('Send troops from every enabled Castle Defense location using the saved settings?'),
                                'send'
                            )}
                            sx={{
                                minHeight: 38,
                                borderRadius: 1.5,
                                color: '#fff',
                                backgroundColor: '#0f9f78',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#0b8465',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {manualCastleDefenseAction === 'send' ? t('Working...') : t('Send troops to protection')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
            <Button
                fullWidth
                variant="text"
                endIcon={individualProtectionExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                aria-expanded={individualProtectionExpanded}
                aria-controls="individual-castle-protection-actions"
                data-testid="individual-protection-toggle"
                onClick={() => setIndividualProtectionExpanded(previous => !previous)}
                sx={{
                    mt: 0.55,
                    minHeight: 34,
                    color: 'text.secondary',
                    justifyContent: 'space-between'
                }}
            >
                {t('Send from a specific castle or outpost')}
            </Button>
            <Collapse in={individualProtectionExpanded} timeout="auto" unmountOnExit>
                <Grid
                    id="individual-castle-protection-actions"
                    data-testid="individual-protection-actions"
                    container
                    spacing={0.75}
                    sx={{ pt: 0.45 }}
                >
                    {CASTLE_DEFENSE_MANUAL_LOCATIONS.map(location => {
                        const locationEnabled = Boolean(
                            user?.plugins?.flee?.castleDefenseConfig?.locations?.[location.key]?.sendTroopsEnabled
                        )
                        const pendingKey = `send-${location.key}`
                        const locationAvailable = canUseManualCastleDefense && locationEnabled
                        const locationDisplayName = resolveCastleDefenseLocationDisplayName(status, location)

                        return (
                            <Grid item xs={12} sm={6} md={4} key={location.key}>
                                <Tooltip
                                    title={locationAvailable ?
                                        t('Send this location to protection') :
                                        locationEnabled ?
                                            t('Castle Defense must be enabled and the bot must be running.') :
                                            t('Enable troop sending for this location in Castle Defense settings.')}
                                >
                                    <span style={{ display: 'flex' }}>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            startIcon={<ShieldRoundedIcon sx={{ fontSize: 17 }} />}
                                            disabled={!locationAvailable || Boolean(manualCastleDefenseAction)}
                                            onClick={() => sendManualCastleDefenseAction(
                                                ActionType.ManualCastleDefenseSend,
                                                `${t('Send troops from this Castle Defense location using its saved settings?')} ${t(location.label)}`,
                                                pendingKey,
                                                location.key
                                            )}
                                            sx={{
                                                minHeight: 34,
                                                justifyContent: 'flex-start',
                                                color: '#6ee7b7',
                                                borderColor: 'rgba(52,211,153,0.38)'
                                            }}
                                        >
                                            {manualCastleDefenseAction === pendingKey ? t('Working...') : locationDisplayName}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        )
                    })}
                </Grid>
            </Collapse>
        </Box>
    )

    const activeModulesPanel = (
        <Box data-testid="active-modules-panel">
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('Modules')}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
                {activePluginNames.length > 0 ? activePluginNames.map(name => (
                    <Chip key={name} size="small" label={name} />
                )) : (
                    <Chip size="small" label={t('No active modules')} />
                )}
            </Stack>
        </Box>
    )

    const dailyAttackCard = (
        <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
            <Box sx={detailCardSx}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('Daily attacks')}
                </Typography>
                {dailyAttackSummaryEntry ? (
                    <>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700 }}>
                            {t(dailyAttackSummaryEntry.label || 'All attacks')}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 700 }}>
                            {numberFormatter.format(dailyAttackCount)}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.55 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {`${t('Attack limit')}: ${numberFormatter.format(dailyAttackServerLimit)}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {`${Math.round(dailyAttackProgress)}%`}
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={dailyAttackProgress}
                            sx={{
                                mt: 0.7,
                                height: 6,
                                borderRadius: 999,
                                backgroundColor: 'rgba(111,157,255,0.18)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    backgroundColor: dailyAttackSummaryEntry.reached ? '#f48484' : '#6f9dff'
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: 'text.secondary' }}>
                            {`${t('Remaining today')}: ${numberFormatter.format(dailyAttackRemaining)}`}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: 'text.secondary' }}>
                            {`${t('Count source')}: ${dailyAttackCountSourceLabel}`}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: 'text.secondary' }}>
                            {`${t('Reset at')}: ${formatDashboardDateTime(dailyAttackSummaryEntry.resetAt)}`}
                        </Typography>
                        {stormVipRemaining !== undefined ? (
                            <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: stormDailyAttackEntry?.vipCommanderLimitReached ? '#fca5a5' : 'text.secondary' }}>
                                {`${t('Storm VIP left today')}: ${numberFormatter.format(stormVipRemaining)} / ${numberFormatter.format(stormVipUsageLimit)}`}
                            </Typography>
                        ) : null}
                        {stormVipRemaining !== undefined ? (
                            <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: stormDailyAttackEntry?.vipCommanderLimitReached ? '#fca5a5' : 'text.secondary' }}>
                                {stormDailyAttackEntry?.vipCommanderLimitReached ?
                                    t('Storm VIP limit reached') :
                                    `${t('Storm VIP used today')}: ${numberFormatter.format(stormVipUsageCount)}`}
                            </Typography>
                        ) : null}
                        <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: 'text.secondary' }}>
                            {dailyAttackSummaryEntry.reached ?
                                t('Threshold reached today') :
                                `${t('Last sync')}: ${formatDashboardClockTime(dailyAttackSummaryEntry.updatedAt)}`}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700 }}>
                            {t('No attack counters yet')}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.55, display: 'block', color: 'text.secondary' }}>
                            {t('Attack counters appear after the first successful hit.')}
                        </Typography>
                    </>
                )}
            </Box>
        </Grid>
    )

    const protectionMovementsContent = (
        <>


                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Protection movements')}
                            </Typography>
                            {troopProtection.movements.length > 0 ? (
                                <Stack spacing={0.5} sx={{ mt: 0.65 }}>
                                    {troopProtection.movements.map(movement => {
                                        const movementTone = getProtectionMovementCardTone(movement)
                                        const movementDetailItems = [
                                            { key: 'troops', label: t('Troops'), value: numberFormatter.format(movement.troopCount), valueColor: movementTone.troopColor },
                                            movement.sourceCoords && movement.sourceCoords !== movement.sourceCastleLabel ?
                                                { key: 'source', label: t('Source'), value: movement.sourceCoords } :
                                                null,
                                            { key: 'receiver', label: t('Receiver'), value: movement.targetPlayerName || t('Unavailable') },
                                            { key: 'destination', label: t('Destination'), value: movement.targetLabel },
                                            movement.movementCount > 1 ?
                                                { key: 'movement', label: t('Movement'), value: `${movement.movementNumber}/${movement.movementCount}` } :
                                                null,
                                            {
                                                key: 'travel-left',
                                                label: t('Travel left'),
                                                value: movement.travelRemainingMs !== undefined ? formatDuration(movement.travelRemainingMs) : t('Unavailable')
                                            },
                                            {
                                                key: 'travel-time',
                                                label: t('Travel time'),
                                                value: movement.travelDurationMs !== undefined ? formatDuration(movement.travelDurationMs) : t('Unavailable')
                                            },
                                            {
                                                key: 'station-left',
                                                label: t('Station left'),
                                                value: movement.stationRemainingMs !== undefined ? formatDuration(movement.stationRemainingMs) : t('Unavailable')
                                            },
                                            {
                                                key: 'station-time',
                                                label: t('Station time'),
                                                value: movement.stationDurationMs !== undefined ? formatDuration(movement.stationDurationMs) : t('Unavailable')
                                            },
                                            {
                                                key: 'departed',
                                                label: t('Departed'),
                                                value: movement.travelStartedAt !== undefined ? formatDashboardDateTime(movement.travelStartedAt) : t('Unavailable')
                                            },
                                            {
                                                key: 'arrival',
                                                label: t('Arrival'),
                                                value: movement.travelArrivalAt !== undefined ? formatDashboardDateTime(movement.travelArrivalAt) : t('Unavailable')
                                            },
                                            {
                                                key: 'station-ends',
                                                label: t('Station ends'),
                                                value: movement.stationEndsAt !== undefined ? formatDashboardDateTime(movement.stationEndsAt) : t('Unavailable')
                                            },
                                            {
                                                key: 'updated',
                                                label: t('Last update'),
                                                value: formatDashboardDateTime(movement.lastSeenAt ?? movement.updatedAt ?? troopProtection.updatedAt)
                                            },
                                            movement.serverReportedAt !== undefined ?
                                                {
                                                    key: 'gge-timer',
                                                    label: t('GGE timer'),
                                                    value: `${movement.serverRemainingSeconds !== undefined ? formatDuration(movement.serverRemainingSeconds * 1000) : t('Unavailable')} / ${movement.serverTotalSeconds !== undefined ? formatDuration(movement.serverTotalSeconds * 1000) : t('Unavailable')}`
                                                } :
                                                null
                                        ].filter(Boolean)

                                        return (
                                        <Box
                                            key={movement.movementId ?? movement.movementKey ?? `${movement.sourceCastleLabel}-${movement.targetLabel}`}
                                            sx={{
                                                p: 0.68,
                                                borderRadius: 1.5,
                                                backgroundColor: movementTone.backgroundColor,
                                                border: `1px solid ${movementTone.borderColor}`,
                                                borderLeft: `4px solid ${movementTone.accentColor}`
                                            }}
                                        >
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.45} justifyContent="space-between">
                                                <Typography variant="caption" sx={{ color: movementTone.headingColor, fontWeight: 800, overflowWrap: 'anywhere' }}>
                                                    {movement.sourceCastleLabel}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: troopProtectionStatusColor(movement), fontWeight: 800 }}>
                                                    {troopProtectionStatusLabel(movement)}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.72} useFlexGap flexWrap="wrap" sx={{ mt: 0.35 }}>
                                                {movementDetailItems.map(item => (
                                                    <Typography
                                                        key={item.key}
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            lineHeight: 1.25,
                                                            overflowWrap: 'anywhere'
                                                        }}
                                                    >
                                                        <Box component="span" sx={{ color: movementTone.labelColor, fontWeight: 800 }}>
                                                            {`${item.label}: `}
                                                        </Box>
                                                        <Box component="span" sx={{ color: item.valueColor ?? movementTone.valueColor }}>
                                                            {item.value}
                                                        </Box>
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )})}
                                </Stack>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 0.55, display: 'block', color: 'text.secondary' }}>
                                    {t('No troops are under protection right now')}
                                </Typography>
                            )}
        </>
    )

    const troopStockCard = (
                    <Grid item xs={12}>
                        <Box data-testid="dashboard-troop-stock" sx={detailCardSx}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Troop stock')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700 }}>
                                        {`${numberFormatter.format(troopStock.total)} ${t('Soldiers')}`}
                                    </Typography>
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Protected troops')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700, textAlign: { xs: 'left', sm: 'right' } }}>
                                        {numberFormatter.format(troopProtection.totalTroops)}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.9 }}>
                                <Chip size="small" label={`${t('Attack')}: ${numberFormatter.format(troopStock.attack)}`} />
                                <Chip size="small" label={`${t('Defense')}: ${numberFormatter.format(troopStock.defense)}`} />
                                <Chip size="small" label={`${t('Melee')}: ${numberFormatter.format(troopStock.melee)}`} />
                                <Chip size="small" label={`${t('Ranged')}: ${numberFormatter.format(troopStock.ranged)}`} />
                                {troopStock.stronghold > 0 ? (
                                    <Chip size="small" label={`${t('Stronghold')}: ${numberFormatter.format(troopStock.stronghold)}`} />
                                ) : null}
                                {troopStock.hospital > 0 ? (
                                    <Chip size="small" label={`${t('Hospital')}: ${numberFormatter.format(troopStock.hospital)}`} />
                                ) : null}
                                {troopStock.traveling > 0 ? (
                                    <Chip size="small" label={`${t('Traveling')}: ${numberFormatter.format(troopStock.traveling)}`} />
                                ) : null}
                            </Stack>

                            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />

                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Castles')}
                            </Typography>
                            {troopStock.castles.length > 0 ? (
                                <Stack spacing={0.75} sx={{ mt: 0.75, maxHeight: 250, overflowY: 'auto', pr: 0.4 }}>
                                    {troopStock.castles.map(castle => (
                                        <Box
                                            key={`${castle.kingdomID ?? 'k'}-${castle.areaID ?? castle.castleLabel}`}
                                            sx={{
                                                p: 0.85,
                                                borderRadius: 1.8,
                                                backgroundColor: 'rgba(255,255,255,0.025)',
                                                border: '1px solid rgba(255,255,255,0.06)'
                                            }}
                                        >
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.45} justifyContent="space-between">
                                                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 800, overflowWrap: 'anywhere' }}>
                                                    {castle.castleLabel}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 800 }}>
                                                    {`${numberFormatter.format(castle.total)} ${t('Troops')}`}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.45 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {`${t('Attack')}: ${numberFormatter.format(castle.attack)}`}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {`${t('Defense')}: ${numberFormatter.format(castle.defense)}`}
                                                </Typography>
                                                {castle.stronghold > 0 ? (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {`${t('Stronghold')}: ${numberFormatter.format(castle.stronghold)}`}
                                                    </Typography>
                                                ) : null}
                                                {castle.hospital > 0 ? (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {`${t('Hospital')}: ${numberFormatter.format(castle.hospital)}`}
                                                    </Typography>
                                                ) : null}
                                                {castle.traveling > 0 ? (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {`${t('Traveling')}: ${numberFormatter.format(castle.traveling)}`}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                            {castle.units.length > 0 ? (
                                                <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
                                                    {castle.units.map(unit => bthMode ? (
                                                        <TroopStockUnitChip
                                                            key={`${castle.areaID ?? castle.castleLabel}-${unit.unitID}`}
                                                            unit={unit}
                                                            amountLabel={numberFormatter.format(unit.amount)}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            key={`${castle.areaID ?? castle.castleLabel}-${unit.unitID}`}
                                                            size="small"
                                                            label={`${unit.label}: ${numberFormatter.format(unit.amount)}`}
                                                            sx={{
                                                                maxWidth: '100%',
                                                                '& .MuiChip-label': {
                                                                    overflowWrap: 'anywhere',
                                                                    whiteSpace: 'normal'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : null}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 0.55, display: 'block', color: 'text.secondary' }}>
                                    {t('No troop stock data yet')}
                                </Typography>
                            )}

                            {!bthMode ? (
                                <>
                                    <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
                                    {protectionMovementsContent}
                                </>
                            ) : null}
                        </Box>
                    </Grid>
    )

    const expandedDetails = (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 1.4 }}>
                <Grid container spacing={1.2}>
                    {bthMode ? (
                        <Grid item xs={12}>
                            <BthSkipInventoryCard user={user} status={status} appLanguage={appLanguage} />
                        </Grid>
                    ) : null}
                    {bthMode ? troopStockCard : null}
                    {allianceRift.active ? (
                        <Grid item xs={12}>
                            <AllianceRiftDashboardCard
                                portal={allianceRift}
                                allianceName={allianceName}
                                t={t}
                            />
                        </Grid>
                    ) : null}
                    {grandTournament.active ? (
                        <Grid item xs={12}>
                            <GrandTournamentDashboardCard
                                tournament={grandTournament}
                                allianceName={allianceName}
                                t={t}
                            />
                        </Grid>
                    ) : null}
                    {!bthMode ? (
                    <Grid item xs={12} sm={layout === 'panel' ? 8 : 12}>
                        <Box sx={detailCardSx}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Player rankings')}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                {t(kingdomLeagueSummary.caption)}
                            </Typography>
                            <Stack spacing={0.55} sx={{ mt: 0.75 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Season rank')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {leagueSeasonRankLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Division rank')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {leagueDivisionRankLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Division')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {leagueDivisionIdLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('players')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {leagueDivisionSizeLabel}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {leaguePlayersHeadingLabel}
                            </Typography>
                            {kingdomLeagueTopEntries.length > 0 ? (
                                <Stack spacing={0.45} sx={{ mt: 0.65 }}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '3ch minmax(0,1fr) minmax(5.4rem,auto) 3ch 3ch 3ch',
                                            columnGap: 0.65,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Rank')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 0 }}>
                                            {t('Name')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                                            {t('Points')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#facc15', textAlign: 'right', fontWeight: 700 }}>
                                            {t('Gold').slice(0, 1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#93c5fd', textAlign: 'right', fontWeight: 700 }}>
                                            {t('Silver').slice(0, 1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#fb923c', textAlign: 'right', fontWeight: 700 }}>
                                            {t('Bronze').slice(0, 1)}
                                        </Typography>
                                    </Box>
                                    {kingdomLeagueTopEntries.map(entry => (
                                        <Box
                                            key={`${entry.rank}-${entry.playerName}`}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '3ch minmax(0,1fr) minmax(5.4rem,auto) 3ch 3ch 3ch',
                                                columnGap: 0.65,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {entry.rank}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.primary',
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {entry.playerName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {formatDashboardWholeNumber(entry.points)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#facc15', textAlign: 'right', fontWeight: 700 }}>
                                                {formatDashboardWholeNumber(entry.goldMedals, '0')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#93c5fd', textAlign: 'right', fontWeight: 700 }}>
                                                {formatDashboardWholeNumber(entry.silverMedals, '0')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#fb923c', textAlign: 'right', fontWeight: 700 }}>
                                                {formatDashboardWholeNumber(entry.bronzeMedals, '0')}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 0.65, display: 'block', color: 'text.secondary' }}>
                                    {leaguePlayersEmptyLabel}
                                </Typography>
                            )}
                            {Number.isFinite(kingdomLeagueLeaderboardUpdatedAt) && kingdomLeagueLeaderboardUpdatedAt > 0 ? (
                                <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Last sync')}: ${formatDashboardClockTime(kingdomLeagueLeaderboardUpdatedAt)}`}
                                </Typography>
                            ) : null}
                        </Box>
                    </Grid>
                    ) : null}
                    {berriWorldEnabled ? (
                        <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
                            <Box sx={detailCardSx}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('Berri World ranking')}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {t(berriWorldSummary.caption)}
                                </Typography>
                                <Stack spacing={0.55} sx={{ mt: 0.75 }}>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Bravery ranking')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {berriWorldRankLabel}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Bravery points')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {berriWorldPointsLabel}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Bracket')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {berriWorldLootIDLabel}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {berriWorldPlayersHeadingLabel}
                                </Typography>
                                {berriWorldTopEntries.length > 0 ? (
                                    <Stack spacing={0.45} sx={{ mt: 0.65 }}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '3ch minmax(0,1fr) minmax(5.4rem,auto)',
                                                columnGap: 0.65,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {t('Rank')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 0 }}>
                                                {t('Name')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                                                {t('Points')}
                                            </Typography>
                                        </Box>
                                        {berriWorldTopEntries.map(entry => (
                                            <Box
                                                key={`berri-${entry.rank}-${entry.playerName}`}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '3ch minmax(0,1fr) minmax(5.4rem,auto)',
                                                    columnGap: 0.65,
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {entry.rank}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.primary',
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    {entry.playerName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {formatDashboardWholeNumber(entry.points)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="caption" sx={{ mt: 0.65, display: 'block', color: 'text.secondary' }}>
                                        {t('No Berri World ranking yet')}
                                    </Typography>
                                )}
                                {Number.isFinite(berriWorldLeaderboardUpdatedAt) && berriWorldLeaderboardUpdatedAt > 0 ? (
                                    <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: 'text.secondary' }}>
                                        {`${t('Last sync')}: ${formatDashboardClockTime(berriWorldLeaderboardUpdatedAt)}`}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Grid>
                    ) : null}
                    {!bthMode ? (
                    <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
                        <Box sx={detailCardSx}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Defense monitoring')}
                            </Typography>
                            <Stack spacing={0.55} sx={{ mt: 0.65 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Khan active')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: incomingKhanTauntCount > 0 ? '#fca5a5' : 'inherit' }}>
                                        {numberFormatter.format(incomingKhanTauntCount)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Khan attacks (30m)')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {numberFormatter.format(recentKhanAttackCount)}
                                    </Typography>
                                </Stack>
                                {Number.isFinite(lastKhanAttackAt) && lastKhanAttackAt > 0 ? (
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Last Khan seen')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            {formatDashboardClockTime(lastKhanAttackAt)}
                                        </Typography>
                                    </Stack>
                                ) : null}
                            </Stack>
                            <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                            {hasKhanToolStatus ? (
                                <>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Khan wall tools')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: khanToolsMissingCount > 0 ? '#fbbf24' : '#86efac'
                                            }}
                                        >
                                            {`${numberFormatter.format(khanToolsEquippedCount)}/${numberFormatter.format(khanToolsTargetCount)}`}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: khanToolsMissingCount > 0 ? '#fbbf24' : '#86efac' }}>
                                        {khanToolsMissingCount > 0 ?
                                            `${t('Missing')}: ${numberFormatter.format(khanToolsMissingCount)}` :
                                            t('Complete')}
                                    </Typography>
                                    {khanToolsRefillThreshold !== undefined ? (
                                        <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: 'text.secondary' }}>
                                            {`${t('Refill counter')}: ${numberFormatter.format(khanToolsPendingHits)}/${numberFormatter.format(khanToolsRefillThreshold)}`}
                                        </Typography>
                                    ) : null}
                                    {Number.isFinite(khanToolsLastRefilledAt) && khanToolsLastRefilledAt > 0 ? (
                                        <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: 'text.secondary' }}>
                                            {`${t('Tools restored')}: +${numberFormatter.format(khanToolsLastRefilledCount)} (${formatDashboardClockTime(khanToolsLastRefilledAt)})`}
                                        </Typography>
                                    ) : null}
                                </>
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('Unavailable')}
                                </Typography>
                            )}
                            <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                            {defensiveBattlesRead > 0 && lastDefensiveTroopsLost !== undefined ? (
                                <Stack spacing={0.55}>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Last defense losses')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: lastDefensiveTroopsLost > 0 ? '#fca5a5' : '#86efac' }}>
                                            {numberFormatter.format(lastDefensiveTroopsLost)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Wounded in last defense')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {numberFormatter.format(lastDefensiveWounded)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Defense losses this session')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {numberFormatter.format(defensiveTroopsLost)}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {`${t('Last defense')}: ${status?.lastDefenseWon ? t('Victory') : t('Defeat')}${lastDefenseAttackerName ? ` | ${lastDefenseAttackerName}` : ''}${Number.isFinite(lastDefenseReportAt) ? ` | ${formatDashboardClockTime(lastDefenseReportAt)}` : ''}`}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('No defense reports yet')}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    ) : null}
                    <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
                        <Box sx={detailCardSx}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {`${t('Coins')} & ${t('Rubies')}`}
                            </Typography>
                            <Stack spacing={0.75} sx={{ mt: 0.7 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Coins')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {coinsLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Rubies')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {rubiesLabel}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Battle rewards')}
                            </Typography>
                            <Stack spacing={0.55} sx={{ mt: 0.65 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Rubies earned')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {numberFormatter.format(earnedRubiesValue)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Coins earned')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {numberFormatter.format(earnedCoinsValue)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Aquamarine earned')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {numberFormatter.format(earnedAquamarineValue)}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: battleReportsRead > 0 ? 'text.secondary' : '#fbbf24' }}>
                                {rewardReportStatusLabel}
                            </Typography>
                            {battleReportsRead > 0 ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Last report')}: ${formatDashboardClockTime(lastBattleReportAt)}`}
                                </Typography>
                            ) : null}
                        </Box>
                    </Grid>
                    {!bthMode ? (
                    <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
                        <Box sx={detailCardSx}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Aquamarine')}
                            </Typography>
                            <Stack spacing={0.55} sx={{ mt: 0.65 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('On hand')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {aquamarineLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Contest points')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {internalContestPointsLabel}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Divider sx={{ my: 0.9, borderColor: 'rgba(255,255,255,0.06)' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Internal alliance contest')}
                            </Typography>
                            <Stack spacing={0.55} sx={{ mt: 0.65 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Your rank')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {internalContestRankLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Points')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {internalContestPointsLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('Alliance rank')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {allianceStormRankLabel}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Grid>
                    ) : null}
                    {dailyAttackCard}
                    <Grid item xs={12} sm={layout === 'panel' ? 4 : 6}>
                        <Box sx={alertCardSx}>
                            <Typography variant="caption" sx={{ color: resourceAlert.hasAlert ? '#fca5a5' : 'text.secondary' }}>
                                {resourceAlert.hasAlert ? t('ATTENTION') : t('Resources stable')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700, color: resourceAlert.hasAlert ? '#ffe2e2' : 'inherit' }}>
                                {resourceAlert.hasAlert ? t('Resource warnings') : t('Food | Honey | Mead')}
                            </Typography>
                            {resourceAlert.hasAlert ? (
                                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                                    {resourceAlertDetails.map(detail => (
                                        <Box
                                            key={detail.key}
                                            sx={{
                                                p: 0.8,
                                                borderRadius: 1.8,
                                                backgroundColor: 'rgba(127,29,29,0.18)',
                                                border: '1px solid rgba(248,113,113,0.2)'
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={0.45}
                                                justifyContent="space-between"
                                                sx={{ minWidth: 0 }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#ffe2e2',
                                                        fontWeight: 800,
                                                        overflowWrap: 'anywhere',
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {detail.castleLabel}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#fecaca',
                                                        fontWeight: 800,
                                                        overflowWrap: 'anywhere',
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {`${detail.resourceLabel} ${detail.rateLabel}`}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={{ xs: 0.15, sm: 1 }}
                                                useFlexGap
                                                flexWrap="wrap"
                                                sx={{ mt: 0.35 }}
                                            >
                                                <Typography variant="caption" sx={{ color: '#fecaca', lineHeight: 1.35 }}>
                                                    {`${t('Depletes in')}: ${detail.depletionLabel}`}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#fecaca', lineHeight: 1.35 }}>
                                                    {`${t('Current stock')}: ${detail.currentStockLabel}`}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 0.55,
                                        display: 'block',
                                        color: 'text.secondary',
                                        whiteSpace: 'normal',
                                        overflowWrap: 'anywhere',
                                        lineHeight: 1.45
                                    }}
                                >
                                    {t('Food | Honey | Mead')}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    {!bthMode ? troopStockCard : (
                        <Grid item xs={12}>
                            <Box sx={detailCardSx}>
                                {protectionMovementsContent}
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <OfferOverviewCard
                            overview={status?.offerOverview}
                            user={user}
                            ws={ws}
                            now={now}
                            t={t}
                        />
                    </Grid>
                </Grid>

                {layout !== 'panel' ? (
                    <Button variant="text" startIcon={<ChatRoundedIcon />} onClick={onOpenChat} sx={{ mt: 1.2 }}>
                        {t('Alliance Chat')}
                    </Button>
                ) : null}
            </Box>
        </Collapse>
    )

    if (layout === 'list') {
        return (
            <Card data-no-ui-translate="true" sx={{ borderRadius: 2.8, transition: 'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease', ...attackCardSx }}>
                <CardContent sx={{ px: 2, py: 1.45 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={stateLabel}
                                    variant="outlined"
                                    sx={{
                                        color: stateChipColor,
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        backgroundColor: 'rgba(255,255,255,0.04)'
                                    }}
                                />
                                {isStandby && standbyCaption ? (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {standbyCaption}
                                    </Typography>
                                ) : null}
                                <Typography variant="h6" sx={{ flex: 1, fontSize: '1.05rem' }}>
                                    {user?.name}
                                </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ mt: 0.55, display: 'block', color: 'text.secondary' }}>
                                {`${t('Rubies earned')}: ${numberFormatter.format(earnedRubiesValue)}`}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: incomingKhanTauntCount > 0 ? '#fca5a5' : 'text.secondary' }}>
                                {`${t('Khan active')}: ${numberFormatter.format(incomingKhanTauntCount)} | ${t('Khan attacks (30m)')}: ${numberFormatter.format(recentKhanAttackCount)}`}
                            </Typography>
                            {hasKhanToolStatus ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: khanToolsMissingCount > 0 ? '#fbbf24' : '#86efac' }}>
                                    {`${t('Khan wall tools')}: ${numberFormatter.format(khanToolsEquippedCount)}/${numberFormatter.format(khanToolsTargetCount)}`}
                                </Typography>
                            ) : null}
                            {lastDefensiveTroopsLost !== undefined ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: lastDefensiveTroopsLost > 0 ? '#fca5a5' : 'text.secondary' }}>
                                    {`${t('Last defense losses')}: ${numberFormatter.format(lastDefensiveTroopsLost)}`}
                                </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: battleReportsRead > 0 ? 'text.secondary' : '#fbbf24' }}>
                                {rewardReportStatusLabel}
                            </Typography>
                            {battleReportsRead > 0 ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Last report')}: ${formatDashboardClockTime(lastBattleReportAt)}`}
                                </Typography>
                            ) : null}
                            {attackAlertPanel}
                        </Box>
                        {quickActions}
                    </Stack>
                    <Box sx={{ mt: 1.15 }}>
                        {castleDefenseActions}
                    </Box>
                    <Box sx={{ mt: 1.15 }}>
                        {activeModulesPanel}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    if (layout === 'grid') {
        return (
            <Card data-no-ui-translate="true" sx={{ height: '100%', borderRadius: 3, transition: 'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease', ...attackCardSx }}>
                <CardContent sx={{ p: 1.7 }}>
                    <Stack direction="row" spacing={1.2} justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.9 }}>
                                <Chip
                                    size="small"
                                    label={stateLabel}
                                    variant="outlined"
                                    sx={{
                                        color: stateChipColor,
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        backgroundColor: 'rgba(255,255,255,0.04)'
                                    }}
                                />
                                {isStandby && standbyCaption ? (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {standbyCaption}
                                    </Typography>
                                ) : null}
                                {underAttack && <WarningAmberRoundedIcon sx={{ color: 'error.main', fontSize: 18 }} />}
                            </Stack>
                            <Typography variant="h6" sx={{ fontSize: '1.05rem', lineHeight: 1.25, wordBreak: 'break-word' }}>
                                {user?.name}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.6, display: 'block', color: 'text.secondary' }}>
                                {`${t('Rubies earned')}: ${numberFormatter.format(earnedRubiesValue)}`}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: incomingKhanTauntCount > 0 ? '#fca5a5' : 'text.secondary' }}>
                                {`${t('Khan active')}: ${numberFormatter.format(incomingKhanTauntCount)} | ${t('Khan attacks (30m)')}: ${numberFormatter.format(recentKhanAttackCount)}`}
                            </Typography>
                            {hasKhanToolStatus ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: khanToolsMissingCount > 0 ? '#fbbf24' : '#86efac' }}>
                                    {`${t('Khan wall tools')}: ${numberFormatter.format(khanToolsEquippedCount)}/${numberFormatter.format(khanToolsTargetCount)}`}
                                </Typography>
                            ) : null}
                            {lastDefensiveTroopsLost !== undefined ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: lastDefensiveTroopsLost > 0 ? '#fca5a5' : 'text.secondary' }}>
                                    {`${t('Last defense losses')}: ${numberFormatter.format(lastDefensiveTroopsLost)}`}
                                </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: battleReportsRead > 0 ? 'text.secondary' : '#fbbf24' }}>
                                {rewardReportStatusLabel}
                            </Typography>
                            {battleReportsRead > 0 ? (
                                <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Last report')}: ${formatDashboardClockTime(lastBattleReportAt)}`}
                                </Typography>
                            ) : null}
                            {attackAlertPanel}
                        </Box>
                        {quickActions}
                    </Stack>

                    <Box sx={{ mt: 1.35 }}>
                        {castleDefenseActions}
                    </Box>

                    <Box sx={{ mt: 1.35 }}>
                        {activeModulesPanel}
                    </Box>

                    <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />

                    <Button
                        fullWidth
                        variant="text"
                        endIcon={expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                        onClick={() => setExpanded(previous => !previous)}
                        sx={{ color: 'text.secondary' }}
                    >
                        {expanded ? t('Less details') : t('More details')}
                    </Button>

                    {expandedDetails}
                </CardContent>
            </Card>
        )
    }

    return (
        <Card data-no-ui-translate="true" sx={{ borderRadius: 3.2, transition: 'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease', ...attackCardSx }}>
            <CardContent sx={{ p: 2.2 }}>
                <Stack spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                            <Chip
                                size="small"
                                label={stateLabel}
                                variant="outlined"
                                sx={{
                                    color: stateChipColor,
                                    borderColor: 'rgba(255,255,255,0.1)',
                                    backgroundColor: 'rgba(255,255,255,0.04)'
                                }}
                            />
                            <Typography variant="h5" sx={{ fontSize: { xs: '1.35rem', md: '1.5rem' }, lineHeight: 1.15 }}>
                                {user?.name}
                            </Typography>
                            {underAttack && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
                                    label={t('Under attack')}
                                    sx={{
                                        color: '#fecaca',
                                        borderColor: 'rgba(248,113,113,0.76)',
                                        backgroundColor: 'rgba(239,68,68,0.18)',
                                        fontWeight: 700,
                                        '& .MuiChip-icon': {
                                            color: '#fca5a5'
                                        }
                                    }}
                                />
                            )}
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ChatRoundedIcon sx={{ fontSize: 17 }} />}
                                onClick={onOpenChat}
                                data-testid="alliance-chat-shortcut"
                                sx={{
                                    ml: { xs: 0, sm: 'auto' },
                                    minHeight: 30,
                                    px: 1,
                                    color: '#60a5fa',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t('Alliance Chat')}
                            </Button>
                        </Stack>
                        {isStandby && standbyCaption ? (
                            <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: '#facc15' }}>
                                {standbyCaption}
                            </Typography>
                        ) : null}
                        <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}>
                            {`${t('Rubies earned')}: ${numberFormatter.format(earnedRubiesValue)}`}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.3, display: 'block', color: battleReportsRead > 0 ? 'text.secondary' : '#fbbf24' }}>
                            {rewardReportStatusLabel}
                        </Typography>

                        {attackAlertPanel}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 1.35 }}>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('Owner')}: {ownerLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary' }}>
                                    {t('UUID')}: {user?.uuid?.substring(0, 8) || t('Unknown')}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('Alliance')}: {allianceName || t('Unavailable')}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary' }}>
                                    {t('Power')}: {powerLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary' }}>
                                    {t('Honor')}: {honourLabel}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('Approval')}: {approvalSummary.label}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary' }}>
                                    {`${t('Uptime')}: ${uptimeLabel}`}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.8, color: incomingKhanTauntCount > 0 ? '#fca5a5' : 'text.secondary' }}>
                                    {t('Khan active')}: {numberFormatter.format(incomingKhanTauntCount)} | {t('Khan attacks (30m)')}: {numberFormatter.format(recentKhanAttackCount)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.1} alignItems={{ xs: 'stretch', xl: 'center' }}>
                        {showPrimaryAction && (
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={isStandby ? <PlayArrowRoundedIcon /> : active ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
                                onClick={handlePrimaryAction}
                                sx={{
                                    flex: 1,
                                    minHeight: 36,
                                    borderRadius: 2.4,
                                    backgroundColor: isStandby ? '#10b981' : active ? '#ef4444' : '#10b981',
                                    color: '#fff',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: isStandby ? '#059669' : active ? '#dc2626' : '#059669',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {primaryLabel}
                            </Button>
                        )}
                        {isStandby && (
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<StopRoundedIcon />}
                                onClick={handleStopBot}
                                sx={{
                                    flex: 1,
                                    minHeight: 36,
                                    borderRadius: 2.4,
                                    backgroundColor: '#ef4444',
                                    color: '#fff',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#dc2626',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Stop')}
                            </Button>
                        )}

                        <Button variant="outlined" startIcon={<SettingsRoundedIcon />} onClick={onOpenSettings}>
                            {t('Configure')}
                        </Button>
                        <Button variant="outlined" startIcon={<MonitorHeartRoundedIcon />} onClick={onOpenLogs}>
                            {t('Live Ops')}
                        </Button>
                        <IconButton
                            onClick={() => onDelete?.(user)}
                            sx={{
                                width: 48,
                                height: 36,
                                borderRadius: 2.4,
                                backgroundColor: '#ef2f3b',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#dc2626'
                                }
                            }}
                        >
                            <DeleteRoundedIcon />
                        </IconButton>
                    </Stack>

                    {castleDefenseActions}

                    {activeModulesPanel}

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                    <Button
                        variant="text"
                        endIcon={expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                        onClick={() => setExpanded(previous => !previous)}
                        sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
                    >
                        {expanded ? t('Less details') : t('More details')}
                    </Button>

                    {expandedDetails}
                </Stack>
            </CardContent>
        </Card>
    )
}
